import { EleReservationLine } from "../../components/EleReservationLine";
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { OverflowMenu, overflowItemCls, overflowDangerItemCls } from "../../components/OverflowMenu";
import { PostcodeLookup } from "../../components/PostcodeLookup";
import { projectRenameWarning } from "../../lib/projectRename";
import { summariseScope, scopeSummaryIsShorter } from "../../lib/scopeSummary";
import type {
  ProjectDetail,
  ProjectStatus,
  WorksClassification,
  ProjectDocument,
  ProjectDocType,
  DocStatus,
  DocumentSendPreview,
  QuotationDetail,
  ProjectSubcontractor,
  ProjectSubcontractorInput,
  ProjectSubcontractorStatus,
  ProjectInvoice,
  MilestoneInvoiceInput,
  ProjectLinkedSubcontractor,
  SubcontractorListItem,
  ModReExamStatus,
  ProjectModReExamFlag,
  ProjectLabourLine,
  ProjectLabourBasis,
  ProjectSupplierCostLine,
  ProjectQuotationVersion,
  ProjectQuotationLostReasonCode,
} from "../../lib/api";
import { PROJECT_QUOTATION_LOST_REASON_LABELS } from "../../lib/api";
import { ProjectPhotos } from "./ProjectPhotos";
import { computeClientCostSummary, previewMilestones, labourLineCost, labourLineCharge, supplierLineCharge, chargeFor, computeProjectValueFromCosts, DEFAULT_MARKUP_PERCENT } from "./costsUi";
import { useAuth } from "../../lib/auth";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { AgreementTag } from "./subcontractorsUi";
import {
  WORKS_TYPE_LABELS,
  WORKS_TYPE_OPTIONS,
  PROJECT_STATUS_LABELS,
  SUBCONTRACTOR_STATUS_LABELS,
  PROJECT_STAGE_FLOW,
  OFF_FLOW_STATUSES,
  projectStageIndex,
  isBackwardStage,
  stageTabFor,
  previewHrb,
  standardForWorksClass,
  reopenBannerVisible,
  nextProjectAction,
  type NextAction,
  gbp,
  formatBytes,
  type ProjectDetailTab,
  type ProjectFocusSection,
} from "./projectsUi";
import { sites, type Customer, type Site } from "../../lib/resources";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { RecordPicker, type PickerRecord } from "../../components/RecordPicker";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import ProjectWorksScheduler from "./ProjectWorksScheduler";
import { ProjectSurveySection } from "./projectSurvey/ProjectSurveySection";
import { londonToday } from "../../lib/rateCardUi";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const btnSm = "rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 disabled:opacity-50";
// The single solid-green primary CTA per surface (the AAB colour rule: primary actions green).

// The four workflow stages (Session AAB redesign): each tab is a genuine phase of the project,
// not a category of fields. Details = the job, the client, the survey. Costing = the internal
// price build (the customer never sees it). Quote = scope -> approve -> send via Signable.
// Delivery = subcontractors, stage invoicing, the programme and the closeout documents.
const TABS = ["details", "costs", "quote", "delivery"] as const;
type Tab = ProjectDetailTab;
const TAB_LABELS: Record<Tab, string> = {
  details: "Details",
  costs: "Costing",
  quote: "Quote",
  delivery: "Delivery",
};

// Session AEH, FIX-1 A1: a branded replacement for window.confirm (the platform confirm standard
// applies to EVERY consequential action, not only deletes). This promise-based hook lets an async
// handler `await confirm({...})` exactly where it used to call window.confirm, so the send/status
// effect after the gate is byte-identical: only the native dialog becomes a branded ModalShell.
// Each consuming component renders {confirmDialog} once in its JSX.
interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
}

function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((req: ConfirmRequest) => {
    return new Promise<boolean>((resolve) => {
      // If a prior confirm is still pending (a double click), cancel it so its awaiting handler
      // unwinds as "not confirmed" rather than hanging forever on an overwritten resolver.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setRequest(req);
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    resolve?.(ok);
  }, []);

  const confirmDialog = request ? (
    <ModalShell ariaLabel={request.title} onClose={() => settle(false)} closeOnBackdrop>
      <h2 className="font-heading text-lg text-ele-strong">{request.title}</h2>
      <p className="mt-3 text-sm text-ele-body">{request.message}</p>
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={() => settle(false)}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
        >
          {request.cancelLabel ?? "Cancel"}
        </button>
        <Button
          type="button"
          autoFocus
          onClick={() => settle(true)}
          className={MODAL_ACTION_BTN}
        >
          {request.confirmLabel}
        </Button>
      </div>
    </ModalShell>
  ) : null;

  return { confirm, confirmDialog };
}

export function ProjectDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState<Tab>("details");
  // Session AAX Loop 2: the section the next-step CTA lands on within its tab. "client" opens
  // the client edit modal (via a DetailsTab prop); the rest scroll to their data-focus-section
  // element and flash it. Cleared once consumed, or on a manual tab change.
  const [focusSection, setFocusSection] = useState<ProjectFocusSection | null>(null);
  // Session ABB, Loop 2: the "Generate scope" CTA DOES what it says -- it triggers the scope
  // draft on arrival (only when no draft exists yet; it never regenerates over one). One press
  // instead of press-then-find-then-press-again.
  const [autoScopeGenerate, setAutoScopeGenerate] = useState(false);

  // Session AAX Loop 5: reloads refresh IN PLACE. Only the first load (or navigating to a
  // different project) shows the loading state -- an in-page action's reload used to unmount
  // the whole page (and every tab's local state, including "Costs saved." feedback) into a
  // full-page loading flash on every save.
  const load = useCallback(async () => {
    setError(false);
    try {
      setProject(await api.projects.get(id));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // The scroll targets mount only after the tab's own data load, so poll briefly for the
  // element, then scroll to it and flash it. scrollIntoView is guarded (absent under jsdom).
  useEffect(() => {
    if (!focusSection || focusSection === "client") return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.querySelector<HTMLElement>(`[data-focus-section="${focusSection}"]`);
      tries += 1;
      if (el) {
        window.clearInterval(timer);
        el.scrollIntoView?.({ behavior: "smooth", block: "start" });
        el.classList.add("ele-focus-flash");
        window.setTimeout(() => el.classList.remove("ele-focus-flash"), 1800);
        setFocusSection(null);
      } else if (tries > 25) {
        window.clearInterval(timer);
        setFocusSection(null);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [focusSection, tab]);

  if (loading) return <Loader shape="panel" />;
  if (error || !project)
    return (
      <div>
        <Link to="/office/projects" className="text-sm text-ele-green hover:underline">&larr; Back to projects</Link>
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">Could not load the project.</p>
      </div>
    );

  return (
    <div>
      <Header project={project} reload={load} isAdmin={isAdmin} onDeleted={() => navigate("/office/projects")} onNavigateTab={setTab} />
      <ComplianceBanners project={project} reload={load} />
      <NextStepBanner
        project={project}
        currentTab={tab}
        onGo={(t, section, key) => {
          setTab(t);
          setFocusSection(section);
          if (key === "generate_scope") setAutoScopeGenerate(true);
        }}
      />

      {/* Session ACG locked tab treatment: quiet text tabs, full-purple underline on the
          active tab only (.ele-tabs / .ele-tab in index.css). */}
      <div className="ele-tabs mb-4 mt-4">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setFocusSection(null); // a manual tab change abandons any pending section jump
            }}
            className={`ele-tab ${tab === t ? "is-active" : ""}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === "details" ? (
        <DetailsTab
          project={project}
          reload={load}
          isAdmin={isAdmin}
          openClientEditor={focusSection === "client"}
          onClientEditorOpened={() => setFocusSection(null)}
        />
      ) : null}
      {tab === "costs" ? <CostsTab project={project} reload={load} isAdmin={isAdmin} /> : null}
      {tab === "quote" ? (
        <QuoteTab
          project={project}
          reload={load}
          autoGenerateScope={autoScopeGenerate}
          onAutoGenerateHandled={() => setAutoScopeGenerate(false)}
        />
      ) : null}
      {tab === "delivery" ? <DeliveryTab project={project} reload={load} isAdmin={isAdmin} /> : null}
    </div>
  );
}

// The computed next-step banner (Session AAB): one plain sentence saying what to do next and one
// button that goes there, driven by the pure nextProjectAction. Sits under the pipeline so it is
// visible on every tab. Green = do this; amber = waiting on someone else. Hidden when the
// project is archived, off-flow or finished (those states carry their own badges/banners).
function NextStepBanner({
  project,
  currentTab,
  onGo,
}: {
  project: ProjectDetail;
  currentTab: Tab;
  onGo: (tab: Tab, section: ProjectFocusSection | null, key: NextAction["key"]) => void;
}) {
  const action = nextProjectAction({
    status: project.status,
    archivedAt: project.archivedAt,
    customerId: project.customerId,
    clientEmail: project.clientEmail,
    contractValue: project.contractValue,
    scopeSummary: project.scopeSummary,
    hasQuotation: project.hasQuotation,
    quotationOutstanding: project.quotationOutstanding,
    quotationSignedAt: project.quotationSignedAt,
    bsrRequired: project.bsrRequired,
    bsrCleared: project.bsrCleared,
  });
  if (!action) return null;
  const waiting = action.tone === "waiting";
  // Session ABB, Loop 3 (one obvious action per screen): once the user is ALREADY on the tab
  // the CTA points at, that tab's own section primary is the one green action, so the banner
  // keeps its guidance text but drops its button. The client CTA is the exception: it opens
  // the edit modal directly (Session AAX, Loop 2), which no card on the tab does in one press.
  const showCta = Boolean(action.cta && action.tab) && (action.tab !== currentTab || action.section === "client");
  return (
    // Session ACW header pattern: stacks below sm (640px), byte-identical row from sm up.
    <div
      role="status"
      aria-label="Next step"
      className={`mt-4 flex flex-col gap-3 rounded-ele border p-3 sm:flex-row sm:items-center sm:justify-between ${
        waiting ? "border-amber-500/40 bg-amber-500/10" : "border-ele-green bg-ele-surface"
      }`}
    >
      <div>
        <div className={`font-menu text-[10px] font-bold uppercase tracking-widest ${waiting ? "text-amber-300" : "text-ele-green"}`}>
          {waiting ? "Waiting" : "Next step"}
        </div>
        <p className="mt-0.5 font-heading text-lg text-ele-strong">{action.label}</p>
        <p className="text-sm text-ele-body">{action.hint}</p>
      </div>
      {showCta ? (
        <Button onClick={() => onGo(action.tab!, action.section, action.key)} size="sm">
          {action.cta}
        </Button>
      ) : null}
    </div>
  );
}

// Session AAY: the project reference (ELE-P-NNNN, or any value the office types such as a customer
// PO number) is editable in place by clicking the heading, matching the "click any card to edit it"
// pattern. A plain inline save when the project has no signed/invoiced history; a two-step confirm
// (naming what already references the old value) when it does. Prior references show in a quiet
// expandable note once there is more than one.
function useReferenceHeading(project: ProjectDetail, reload: () => Promise<void>): { title: ReactNode; below: ReactNode } {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.projectRef);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const warning = projectRenameWarning({
    oldRef: project.projectRef,
    hasSignedQuotation: Boolean(project.quotationSignedAt),
    invoiceCount: project.invoiceCount ?? 0,
  });

  function start() {
    setValue(project.projectRef);
    setErr(null);
    setConfirming(false);
    setEditing(true);
  }
  function cancel() {
    setEditing(false);
    setConfirming(false);
    setErr(null);
  }

  async function save() {
    const next = value.trim();
    if (!next) {
      setErr("Enter a reference.");
      return;
    }
    if (next === project.projectRef) {
      cancel();
      return;
    }
    // A project with signed/invoiced history gets a two-step confirm: the first Save surfaces the
    // caution, the second (Rename anyway) commits. No history: save straight away.
    if (warning && !confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.projects.renameReference(project.id, next);
      await reload();
      setEditing(false);
      setConfirming(false);
    } catch (e) {
      // A clash returns a clean 409 with a plain-English message; stay in edit mode so the office
      // can pick another value.
      setErr(e instanceof ApiError ? e.message || e.code : "Could not rename the reference.");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  const priorRefs = project.referenceHistory ?? [];

  if (editing) {
    return {
      title: project.projectRef,
      below: (
      <div className="mt-3 max-w-xl space-y-2">
        <label className={labelCls} htmlFor="project-ref">Project reference</label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="project-ref"
            className={`${inputCls} max-w-xs font-heading tracking-wide`}
            value={value}
            autoFocus
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); void save(); }
              if (e.key === "Escape") { e.preventDefault(); cancel(); }
            }}
          />
          <Button type="button" onClick={() => void save()} disabled={busy} size="sm">
            {busy ? "Saving..." : confirming ? "Rename anyway" : "Save"}
          </Button>
          <button type="button" onClick={cancel} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>Cancel</button>
        </div>
        {confirming && warning ? <p className="text-xs text-amber-300">{warning}</p> : null}
        {err ? <p role="alert" className="text-xs text-ele-danger">{err}</p> : null}
        <p className="text-xs text-ele-body opacity-70">Type any value: an ELE-P number, or the client's own PO or scheme reference.</p>
      </div>
      ),
    };
  }

  return {
    title: (
      <button
        type="button"
        onClick={start}
        aria-label={`Edit the project reference (currently ${project.projectRef})`}
        className="group flex items-center gap-2 rounded-ele text-left outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ele-green"
      >
        {project.projectRef}
        <span className="font-menu text-[11px] tracking-wide text-ele-body opacity-0 transition group-hover:opacity-70 group-focus-visible:opacity-70">Rename</span>
      </button>
    ),
    below:
      priorRefs.length > 1 ? (
        <div className="mt-0.5">
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="font-menu text-[11px] tracking-wide text-ele-blue hover:underline"
          >
            {historyOpen ? "Hide previous references" : `Previous references (${priorRefs.length})`}
          </button>
          {historyOpen ? (
            <p className="mt-1 text-xs text-ele-body opacity-70">Previously: {priorRefs.join(", ")}</p>
          ) : null}
        </div>
      ) : null,
  };
}

function Header({ project, reload, isAdmin, onDeleted, onNavigateTab }: { project: ProjectDetail; reload: () => Promise<void>; isAdmin: boolean; onDeleted: () => void; onNavigateTab?: (tab: Tab) => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  // Session ABN (Item 1): the admin "Reopen for editing" back door -- an inline reason modal,
  // then POST /reopen. Available at ANY status; the office always has a way to correct mistakes.
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  // Soft prompt before completing a Modification project with an open re-exam flag (advisory).
  const [reExamPrompt, setReExamPrompt] = useState(false);
  // Session ATI (Q-8): moving a project to "lost" requires a reason, captured here and recorded
  // on the current quotation version's own ledger row (server-side; RULE 6, one home).
  const [showLostReason, setShowLostReason] = useState(false);
  const [lostReasonCode, setLostReasonCode] = useState<ProjectQuotationLostReasonCode | "">("");
  const [lostReasonNote, setLostReasonNote] = useState("");
  const [lostSaving, setLostSaving] = useState(false);
  const [lostError, setLostError] = useState<string | null>(null);
  const archived = project.archivedAt != null;
  const reference = useReferenceHeading(project, reload);
  // Predict whether the server will archive (vs hard-delete) from what the detail exposes,
  // so the dialog frames the action accurately (amber/reversible vs red/permanent). The
  // server is authoritative (it also checks the milestone-invoice count, which the detail
  // does not carry), so a project with only an invoice still archives even though this
  // predicts a delete -- the response is handled either way.
  const RETAINED_STATUSES: ProjectStatus[] = ["accepted", "works_scheduled", "in_progress", "on_hold", "complete", "invoiced"];
  const willArchive =
    RETAINED_STATUSES.includes(project.status) || project.hasQuotation || project.subcontractors.some((s) => s.hasAgreement);

  const reExamFlag = project.modificationReExamFlag;

  async function changeStatus(status: ProjectStatus) {
    if (status === project.status) return;
    // Session ATI (Q-8): a move TO "lost" requires a reason -- opens the inline modal instead of
    // moving immediately; the modal itself calls doChangeStatus once a reason is chosen.
    if (status === "lost") {
      setLostError(null);
      setLostReasonCode("");
      setLostReasonNote("");
      setShowLostReason(true);
      return;
    }
    // Advisory soft prompt: completing a Modification project with an open re-exam flag. Not a
    // hard stop -- the office can proceed either way, and the prompt response is recorded.
    if (status === "complete" && reExamFlag && reExamFlag.status === "open") {
      setReExamPrompt(true);
      return;
    }
    await doChangeStatus(status);
  }

  // Session AAC, Item 4: the statuses the server's signature gate covers. Mirrored here so a
  // gated move is a DELIBERATE act: the confirm names what is being overridden before the
  // request goes (an admin proceeds and the override is recorded server-side in the audit
  // log; an office user who confirms still gets the server's plain-English 409).
  const SIGNATURE_GATED: ProjectStatus[] = ["accepted", "works_scheduled", "in_progress", "complete", "invoiced"];

  async function doChangeStatus(
    status: ProjectStatus,
    lostReason?: { code: ProjectQuotationLostReasonCode; note: string | null },
  ): Promise<boolean> {
    if (
      SIGNATURE_GATED.includes(status) &&
      project.hasQuotation &&
      !project.quotationSignedAt &&
      !project.acceptedAt &&
      !(await confirm({
        title: "Move the project?",
        message:
          "The quotation is still with the client for signature via Signable. Signing records the acceptance automatically. Move the project anyway? An admin override is recorded in the audit log.",
        confirmLabel: "Move anyway",
      }))
    ) {
      return false;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.update(project.id, {
        status,
        ...(lostReason ? { lostReasonCode: lostReason.code, lostReasonNote: lostReason.note } : {}),
      });
      await reload();
      return true;
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not change the status.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Session AS, Item 5: the interactive progress bar. Clicking a stage moves the project to it. A
  // backward move (which may be accidental) is confirmed first; a forward move goes straight through.
  // Completing a Modification with an open re-examination flag uses the same soft prompt as the
  // dropdown. On success, optionally open the most relevant tab for the new stage.
  async function selectStage(target: ProjectStatus) {
    if (target === project.status) return;
    if (
      isBackwardStage(project.status, target) &&
      !(await confirm({
        title: "Move the project back?",
        message: `Move this project back to ${PROJECT_STATUS_LABELS[target]}?`,
        confirmLabel: "Move back",
      }))
    )
      return;
    if (target === "complete" && reExamFlag && reExamFlag.status === "open") {
      setReExamPrompt(true);
      return;
    }
    const ok = await doChangeStatus(target);
    if (ok) {
      const nextTab = stageTabFor(target);
      if (nextTab) onNavigateTab?.(nextTab);
    }
  }

  // The office's response to the completion prompt: optionally set the flag status, always record
  // that the prompt was shown (completionPrompted), then complete the project (never blocked).
  async function resolveReExamAndComplete(status?: ModReExamStatus) {
    if (!reExamFlag) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.modReExam.updateFlag(reExamFlag.id, status ? { status, completionPrompted: true } : { completionPrompted: true });
    } catch {
      // Recording the response is best-effort; never block the completion the office chose.
    }
    setReExamPrompt(false);
    await doChangeStatus("complete");
  }

  // Delete: the server archives a project with retained history (invoices / a sent or
  // signed agreement / quotation) and hard-deletes an early-stage one. An archived
  // project stays put with a Reactivate action; a hard-deleted one navigates away.
  async function confirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await api.projects.remove(project.id);
      if (res.archived) {
        setShowDelete(false);
        await reload();
      } else {
        onDeleted();
      }
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete the project.");
    } finally {
      setDeleting(false);
    }
  }

  async function reactivate() {
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.reactivate(project.id);
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not reactivate the project.");
    } finally {
      setBusy(false);
    }
  }

  // Reopen the project for editing (admin, any status). The server sets the status back to
  // "quoted", stamps who/when/why and audits it; the signed envelope and documents are untouched.
  async function confirmReopen() {
    if (reopenReason.trim().length < 5) {
      setReopenError("Enter a reason (at least 5 characters).");
      return;
    }
    setReopening(true);
    setReopenError(null);
    try {
      await api.projects.reopen(project.id, reopenReason.trim());
      setShowReopen(false);
      setReopenReason("");
      await reload();
    } catch (e) {
      setReopenError(e instanceof ApiError ? e.message || e.code : "Could not reopen the project.");
    } finally {
      setReopening(false);
    }
  }

  // Session ATI (Q-8): confirm the lost-reason modal, then move the project to "lost" carrying
  // the reason so it lands on the current quotation version's own ledger row.
  async function confirmLost() {
    if (!lostReasonCode) {
      setLostError("Choose a reason.");
      return;
    }
    setLostSaving(true);
    setLostError(null);
    const ok = await doChangeStatus("lost", { code: lostReasonCode, note: lostReasonNote.trim() || null });
    setLostSaving(false);
    if (ok) {
      setShowLostReason(false);
      setLostReasonCode("");
      setLostReasonNote("");
    } else {
      setLostError(msg ?? "Could not mark the project lost.");
    }
  }

  return (
    <div className="mt-2">
      {confirmDialog}
      <RecordHeader
        backTo="/office/projects"
        backLabel="Back to projects"
        title={reference.title}
        status={
          <>
            <StatusPill domain="project" status={project.status} />
            {archived ? <StatusPill word="Archived" tone="amber" variant="outline" /> : null}
          </>
        }
        subtitle={
          <>
            <span className="block text-lg text-ele-strong">{project.title}</span>
            <span className="block">
            {project.worksClassification ? WORKS_TYPE_LABELS[project.worksClassification] : "Not classified"}
            {project.customerName && (
              <>
                {" | "}
                {project.customerId ? (
                  <Link to={`/office/customers/${encodeURIComponent(project.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {project.customerName}
                  </Link>
                ) : (
                  project.customerName
                )}
              </>
            )}
            {project.siteName && (
              <>
                {" | "}
                {project.siteId ? (
                  <Link to={`/office/sites/${encodeURIComponent(project.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {project.siteName}
                  </Link>
                ) : (
                  project.siteName
                )}
              </>
            )}
            </span>
          </>
        }
        actions={
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          {/* Session AAB: the clickable pipeline is the ONE on-path stage control; this select
              covers only the off-path statuses it cannot reach (on hold / lost / cancelled, or
              resuming from one). Session AAC Item 4: while the project is ON the pipeline the
              select is an ACTION menu with a placeholder -- it never displays the current
              status, so it can never read as a second status control (the live confusion:
              "OTHER STATUS" also showing "Works scheduled"). Off the pipeline it is the one
              status control and shows where the project sits. */}
          {(() => {
            const onFlow = projectStageIndex(project.status) >= 0;
            const offFlow = project.allowedNextStatuses.filter(
              (s) => s !== project.status && ((OFF_FLOW_STATUSES as ProjectStatus[]).includes(s) || !onFlow),
            );
            if (offFlow.length === 0) return null;
            return (
              <label
                className="text-xs text-ele-body"
                title={
                  onFlow
                    ? "For taking the project off the pipeline (on hold, lost, cancelled). Pipeline stages are set by clicking the pipeline itself."
                    : "This project is off the delivery pipeline. Choose where to move it."
                }
              >
                <span className="mr-2 font-menu uppercase tracking-wide">{onFlow ? "Take off pipeline" : "Move to"}</span>
                <select
                  className="max-sm:min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green sm:w-auto"
                  value={onFlow ? "" : project.status}
                  disabled={busy}
                  onChange={(e) => { if (e.target.value) changeStatus(e.target.value as ProjectStatus); }}
                >
                  {onFlow ? (
                    <option value="" disabled>Choose...</option>
                  ) : (
                    <option value={project.status} disabled>{PROJECT_STATUS_LABELS[project.status]}</option>
                  )}
                  {offFlow.map((s) => <option key={s} value={s}>{PROJECT_STATUS_LABELS[s]}</option>)}
                </select>
              </label>
            );
          })()}
          {isAdmin ? (
            archived ? (
              <Button onClick={reactivate} disabled={busy} variant="secondary" size="sm">
                {busy ? "Reactivating..." : "Reactivate"}
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {/* Session ABN (Item 1): the admin back door. Any status can be reopened to
                    "quoted" for corrections (nothing is deleted; the signed envelope and every
                    document stay on file). Hidden only at "quoted" itself, where the project is
                    already open for editing and a reopen would change nothing. */}
                {project.status !== "quoted" ? (
                  <button
                    onClick={() => { setReopenError(null); setReopenReason(""); setShowReopen(true); }}
                    disabled={busy}
                    className={`max-sm:min-h-11 ${btnSm} border-amber-500 text-amber-400 hover:bg-amber-500/10`}
                  >
                    Reopen for editing
                  </button>
                ) : null}
                {/* Session AAX Loop 4: the destructive project action lives in a quiet overflow
                    menu, behind the existing branded confirm dialog. Same action, re-weighted. */}
                <OverflowMenu label="Project actions">
                  <button
                    role="menuitem"
                    onClick={() => { setDeleteError(null); setShowDelete(true); }}
                    disabled={busy}
                    className={willArchive ? overflowItemCls : overflowDangerItemCls}
                  >
                    {willArchive ? "Archive project" : "Delete project"}
                  </button>
                </OverflowMenu>
              </div>
            )
          ) : null}
        </div>
        }
      />
      {reference.below}
      {msg ? <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{msg}</p> : null}

      <ProgressIndicator status={project.status} allowedNextStatuses={project.allowedNextStatuses} busy={busy} onSelect={selectStage} />

      <ConfirmDeleteModal
        open={showDelete}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this project?" : "Delete this project?"}
        message={
          willArchive
            ? "This project is a retained record (it is past the enquiry stage, or has invoices, an agreement or a quotation on file). It will be archived and hidden from the active list, and can be reactivated."
            : "This early-stage project has no invoices, agreement or quotation on file and will be permanently deleted, along with its draft documents. If any are on file, it will be archived instead."
        }
        confirmLabel={willArchive ? "Archive project" : "Delete project"}
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) { setShowDelete(false); setDeleteError(null); } }}
      />

      {showReopen ? (
        <ModalShell ariaLabel="Reopen for editing" onClose={() => !reopening && setShowReopen(false)} width="max-w-lg" border="border-amber-500" panelClassName="p-6 text-left">
            <h2 className="font-heading text-xl uppercase tracking-wide text-amber-300">Reopen for editing</h2>
            <p className="mt-2 text-sm text-ele-body">
              This moves the project back to Quoted so it can be corrected. Nothing is deleted: the previous signed
              quotation and every document remain on file. The reason is recorded in the audit trail.
            </p>
            <div className="mt-3">
              <label className={labelCls} htmlFor="reopen-reason">Reason for reopening</label>
              <textarea
                id="reopen-reason"
                className={`${inputCls} min-h-[70px]`}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Pricing error on the accepted quotation; scope line missing"
              />
            </div>
            {reopenError ? <p role="alert" className="mt-2 text-sm text-ele-danger">{reopenError}</p> : null}
            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button onClick={() => { if (!reopening) setShowReopen(false); }} disabled={reopening} className={`${btnSm} border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
              <button onClick={confirmReopen} disabled={reopening || reopenReason.trim().length < 5} className={`${btnSm} border-amber-500 text-amber-400 hover:bg-amber-500/10 ${MODAL_ACTION_BTN}`}>
                {reopening ? "Reopening..." : "Reopen project"}
              </button>
            </div>
        </ModalShell>
      ) : null}

      {showLostReason ? (
        <ModalShell ariaLabel="Mark project lost" onClose={() => !lostSaving && setShowLostReason(false)} width="max-w-lg" border="border-ele-danger" panelClassName="p-6 text-left">
            <h2 className="font-heading text-xl uppercase tracking-wide text-ele-danger">Mark this project lost</h2>
            <p className="mt-2 text-sm text-ele-body">
              Recorded against the current quotation version, so the reason survives alongside what was quoted.
            </p>
            <div className="mt-3">
              <label className={labelCls} htmlFor="lost-reason-code">Reason</label>
              <select
                id="lost-reason-code"
                className={inputCls}
                value={lostReasonCode}
                onChange={(e) => setLostReasonCode(e.target.value as ProjectQuotationLostReasonCode)}
              >
                <option value="" disabled>Choose a reason...</option>
                {Object.entries(PROJECT_QUOTATION_LOST_REASON_LABELS).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
            <div className="mt-3">
              <label className={labelCls} htmlFor="lost-reason-note">Notes (optional)</label>
              <textarea
                id="lost-reason-note"
                className={`${inputCls} min-h-[70px]`}
                value={lostReasonNote}
                onChange={(e) => setLostReasonNote(e.target.value)}
                placeholder="e.g. Went with a cheaper quote from a competitor"
              />
            </div>
            {lostError ? <p role="alert" className="mt-2 text-sm text-ele-danger">{lostError}</p> : null}
            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button onClick={() => { if (!lostSaving) setShowLostReason(false); }} disabled={lostSaving} className={`${btnSm} border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
              <Button onClick={confirmLost} disabled={lostSaving || !lostReasonCode} className={MODAL_ACTION_BTN} variant="destructive" size="sm">
                {lostSaving ? "Saving..." : "Mark lost"}
              </Button>
            </div>
        </ModalShell>
      ) : null}

      {reExamPrompt && reExamFlag ? (
        <ModalShell ariaLabel="LOLER re-examination check" onClose={() => !busy && setReExamPrompt(false)} width="max-w-lg" border="border-amber-500" panelClassName="p-6 text-left">
            <h2 className="font-heading text-xl uppercase tracking-wide text-amber-300">LOLER re-examination check</h2>
            <p className="mt-2 text-sm text-ele-body">
              This work is classified as a modification. A significant modification may require a fresh LOLER thorough
              examination by the duty holder's competent person before the lift returns to service. Has re-examination
              been arranged, or confirmed not required, before this project is completed?
            </p>
            <p className="mt-2 text-xs text-ele-body opacity-80">
              This is a compliance prompt for review, not an automated legal determination. You can proceed either way.
            </p>
            {msg ? <p role="alert" className="mt-2 text-sm text-ele-danger">{msg}</p> : null}
            <div className="mt-4 flex flex-col gap-2">
              <Button onClick={() => resolveReExamAndComplete("re_exam_arranged")} disabled={busy} className={MODAL_ACTION_BTN} variant="secondary" size="sm">
                Re-examination arranged, complete
              </Button>
              <Button onClick={() => resolveReExamAndComplete("confirmed_not_required")} disabled={busy} className={MODAL_ACTION_BTN} variant="secondary" size="sm">
                Confirmed not required, complete
              </Button>
              <button onClick={() => resolveReExamAndComplete(undefined)} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
                Proceed without resolving (recorded)
              </button>
              <button onClick={() => { if (!busy) setReExamPrompt(false); }} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
            </div>
        </ModalShell>
      ) : null}
    </div>
  );
}

// The lifecycle progress indicator at the top of the project page: the happy-path stages as a
// horizontal bar (completed green, current highlighted, future muted). An off-path status
// (on hold / lost / cancelled) greys the bar and shows the status as a plain-language note.
//
// Session AS, Item 5: the bar is interactive. A stage is clickable (a button) when onSelect is given,
// it is not the current stage, and it is a valid next status for this project (so a click never hits
// an invalid-transition 400); every other stage stays a display-only pill. The active pill keeps its
// green styling.
function ProgressIndicator({
  status,
  allowedNextStatuses,
  busy,
  onSelect,
}: {
  status: ProjectStatus;
  allowedNextStatuses?: ProjectStatus[];
  busy?: boolean;
  onSelect?: (status: ProjectStatus) => void;
}) {
  const idx = projectStageIndex(status);
  const offFlow = idx < 0;
  const allowed = new Set(allowedNextStatuses ?? []);
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-1" role="list" aria-label="Project stage">
        {PROJECT_STAGE_FLOW.map((s, i) => {
          const done = !offFlow && i < idx;
          const current = !offFlow && i === idx;
          const clickable = Boolean(onSelect) && !current && allowed.has(s);
          const pillCls = `rounded-full px-2.5 py-1 font-menu text-[10px] uppercase tracking-wide ${
            current ? "bg-ele-green font-bold text-ele-bg" : done ? "bg-ele-inset text-ele-green" : "bg-ele-surface text-ele-body opacity-60"
          }`;
          return (
            <div key={s} role="listitem" aria-current={current ? "step" : undefined} className="flex items-center gap-1">
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onSelect!(s)}
                  disabled={busy}
                  aria-label={`Move to ${PROJECT_STATUS_LABELS[s]}`}
                  title={`Move to ${PROJECT_STATUS_LABELS[s]}`}
                  className={`${pillCls} cursor-pointer outline-none transition hover:brightness-125 hover:ring-1 hover:ring-ele-green focus-visible:ring-1 focus-visible:ring-ele-green disabled:opacity-50`}
                >
                  {PROJECT_STATUS_LABELS[s]}
                </button>
              ) : (
                <span className={pillCls}>{PROJECT_STATUS_LABELS[s]}</span>
              )}
              {i < PROJECT_STAGE_FLOW.length - 1 ? <span className={`h-px w-3 ${done ? "bg-ele-green" : "bg-ele-purple-accent"}`} /> : null}
            </div>
          );
        })}
      </div>
      {offFlow ? <p className="mt-2 text-xs text-amber-400">This project is currently {PROJECT_STATUS_LABELS[status].toLowerCase()}.</p> : null}
    </div>
  );
}

// The BSR and Section 20 warning banners, with the controls to clear them.
function ComplianceBanners({ project, reload }: { project: ProjectDetail; reload: () => Promise<void> }) {
  const [gateDate, setGateDate] = useState(project.bsrGateway2Date ? project.bsrGateway2Date.slice(0, 10) : "");
  const [stage, setStage] = useState(project.section20Stage ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-sync the controls when the project reloads (a PATCH elsewhere changed these),
  // since useState only seeds from props on first mount.
  useEffect(() => {
    setGateDate(project.bsrGateway2Date ? project.bsrGateway2Date.slice(0, 10) : "");
    setStage(project.section20Stage ?? "");
  }, [project.bsrGateway2Date, project.section20Stage]);

  async function saveGate() {
    if (!gateDate) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.update(project.id, { bsrGateway2Date: gateDate });
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not record the date.");
    } finally {
      setBusy(false);
    }
  }

  async function saveStage(value: string) {
    const prev = stage;
    setStage(value); // optimistic
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.update(project.id, { section20Stage: value || null });
      await reload();
    } catch {
      setStage(prev); // revert the optimistic change so the control matches the server
      setMsg("Could not update the consultation stage.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 space-y-2">
      {/* Session ABN (Item 1): the reopen warning. Stays up until the quotation is re-sent to
          the customer (a quotationSentAt LATER than reopenedAt clears it). */}
      {reopenBannerVisible(project.reopenedAt, project.quotationSentAt) ? (
        <div role="alert" className="rounded-ele border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <p className="font-heading uppercase tracking-wide text-amber-300">Project reopened for editing</p>
          <p className="mt-1 text-ele-body">
            This project was reopened on {formatDate(project.reopenedAt)}. The previous signed quotation remains on file.
            Re-send the updated quotation to the customer before proceeding.
          </p>
          {project.reopenReason ? <p className="mt-1 text-xs text-ele-body opacity-80">Reason: {project.reopenReason}</p> : null}
        </div>
      ) : null}
      {project.bsrRequired && !project.bsrCleared ? (
        <div role="alert" className="rounded-ele border border-ele-danger bg-red-500/10 p-3 text-sm">
          <p className="font-heading uppercase tracking-wide text-ele-danger">Higher-Risk Building - BSR Gateway 2 required</p>
          <p className="mt-1 text-ele-body">
            This is a notifiable Higher-Risk Building. Works cannot be scheduled or commenced until the Building Safety
            Regulator's Gateway 2 approval is recorded. Enter the approval date to lift the block.
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div>
              <label className={labelCls}>Gateway 2 approval date</label>
              <input type="date" className={inputCls} value={gateDate} onChange={(e) => setGateDate(e.target.value)} />
            </div>
            <Button onClick={saveGate} disabled={busy || !gateDate} variant="secondary" size="sm">Record approval</Button>
          </div>
        </div>
      ) : null}
      {project.bsrRequired && project.bsrCleared ? (
        <div className="rounded-ele border border-ele-green/40 bg-ele-surface px-3 py-2 text-sm text-ele-green">
          BSR Gateway 2 approval recorded on {formatDate(project.bsrGateway2Date)}. Works may proceed.
        </div>
      ) : null}
      {project.section20Required ? (
        <div className="rounded-ele border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <p className="font-heading uppercase tracking-wide text-amber-300">Section 20 consultation applies</p>
          <p className="mt-1 text-ele-body">
            A leaseholder is charged more than 250 pounds, so the Landlord and Tenant Act 1985 Section 20 consultation
            applies: three stages, minimum 30 days each. Track the current stage.
          </p>
          <div className="mt-2">
            <label className={labelCls}>Consultation stage</label>
            <select className={inputCls + " max-w-xs"} value={stage} disabled={busy} onChange={(e) => saveStage(e.target.value)}>
              <option value="">Not started</option>
              <option value="notice_of_intention">1. Notice of intention</option>
              <option value="estimates">2. Notification of estimates</option>
              <option value="award">3. Notification of award</option>
            </select>
          </div>
        </div>
      ) : null}
      {project.modificationReExamFlag ? <ModReExamBanner flag={project.modificationReExamFlag} reload={reload} /> : null}
      {msg ? <p role="alert" className="rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{msg}</p> : null}
    </div>
  );
}

const MOD_REEXAM_STATUS_LABEL: Record<ModReExamStatus, string> = {
  open: "Open",
  re_exam_arranged: "Re-examination arranged",
  confirmed_not_required: "Confirmed not required",
  dismissed: "Dismissed",
};

// The LOLER re-examination advisory banner for a Modification project. Advisory only: it surfaces
// the compliance prompt and lets the office record the outcome. It never blocks completion.
function ModReExamBanner({ flag, reload }: { flag: ProjectModReExamFlag; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function setStatus(status: ModReExamStatus) {
    setBusy(true);
    setErr(null);
    try {
      await api.modReExam.updateFlag(flag.id, { status });
      await reload();
    } catch {
      setErr("Could not update the re-examination flag. Please try again.");
      setBusy(false);
    }
  }

  if (flag.status !== "open") {
    return (
      <div className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-xs text-ele-body">
        <span className="font-menu uppercase tracking-wide text-ele-blue">LOLER re-examination: {MOD_REEXAM_STATUS_LABEL[flag.status]}</span>
        {flag.note ? <span className="ml-2 opacity-80">Note: {flag.note}</span> : null}
        <button type="button" onClick={() => setStatus("open")} disabled={busy} className="ml-3 text-ele-blue hover:underline disabled:opacity-50">
          Reopen
        </button>
        {err && <p role="alert" className="mt-1 text-ele-danger">{err}</p>}
      </div>
    );
  }

  return (
    <div role="alert" className="rounded-ele border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
      <p className="font-heading uppercase tracking-wide text-amber-300">LOLER re-examination may be required</p>
      <p className="mt-1 text-ele-body">
        This work is classified as a modification. A significant modification may require a fresh LOLER thorough
        examination by the duty holder's competent person before the lift returns to service. Review and confirm
        whether re-examination is required before marking this work complete.
      </p>
      <p className="mt-1 text-xs text-ele-body opacity-80">This is a compliance prompt for review, not an automated legal determination.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={() => setStatus("re_exam_arranged")} disabled={busy} variant="secondary" size="sm">
          Re-examination arranged
        </Button>
        <Button onClick={() => setStatus("confirmed_not_required")} disabled={busy} variant="secondary" size="sm">
          Confirmed not required
        </Button>
        <button onClick={() => setStatus("dismissed")} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>
          Dismiss
        </button>
      </div>
      {err && <p role="alert" className="mt-2 text-ele-danger">{err}</p>}
    </div>
  );
}

// Session AAA Item 4: the single advisory line shown on the Building details card AND its edit
// modal. These are the client's statutory obligations; ELE records them to advise correctly and
// to avoid mobilising before the client has met them.
const BUILDING_ADVISORY_NOTE =
  "These fields record building details relevant to the client's obligations under the Building Safety Act 2022 and Section 20 of the Landlord and Tenant Act 1985. They are advisory and do not represent ELE compliance duties.";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="font-menu text-[11px] uppercase tracking-wide text-ele-body opacity-70">{label}</div>
      <div className="text-sm text-ele-strong">{children}</div>
    </div>
  );
}

// Long free text (the scope wall) collapses to a short preview with a quiet expand toggle
// (Session AAX, Loop 4). The toggle stops propagation so it never opens the card's editor.
function CollapsibleText({ text, limit = 280 }: { text: string; limit?: number }) {
  const [open, setOpen] = useState(false);
  if (text.length <= limit) return <span className="whitespace-pre-wrap">{text}</span>;
  return (
    <span className="whitespace-pre-wrap">
      {open ? text : `${text.slice(0, limit).trimEnd()}...`}{" "}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="font-menu text-[11px] tracking-wide text-ele-blue hover:underline"
      >
        {open ? "Show less" : "Show more"}
      </button>
    </span>
  );
}

// Session AAY (Loop 4): the Details "Scope of works" card shows a genuine short summary, never the
// full itemised, per-lift scope (which reads as a wall of repeated text). The collapsed view is the
// derived summary, hard-capped to three lines; the expand toggle reveals the full scopeSummary
// verbatim (that exact text is what the quotation PDF prints, so it is never altered here). When the
// scope is already short, it is shown plainly with no toggle.
function ScopeSummaryView({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const headline = summariseScope(text);
  const hasMore = scopeSummaryIsShorter(text, headline);
  if (!hasMore) return <span className="whitespace-pre-wrap">{text}</span>;
  return (
    <span>
      {open ? (
        <span className="whitespace-pre-wrap">{text}</span>
      ) : (
        <span className="line-clamp-3 whitespace-pre-wrap">{headline}</span>
      )}{" "}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="font-menu text-[11px] tracking-wide text-ele-blue hover:underline"
      >
        {open ? "Show less" : "Show full scope"}
      </button>
    </span>
  );
}

type OverviewCard = "scope" | "client" | "commercials" | "building";

// A clickable Overview card (Session AU, Item 1): the whole card opens its focused edit modal. A
// visible "Edit" affordance (brighter on hover/focus) makes the interaction discoverable without
// instruction; the card is keyboard-operable (Enter/Space).
function EditableCard({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Edit ${title}`}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      className="group cursor-pointer space-y-3 rounded-ele border border-ele-purple-accent p-4 text-left outline-none transition hover:border-ele-green focus-visible:border-ele-green"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-ele-strong">{title}</h3>
        <span className="font-menu text-[11px] tracking-wide text-ele-body opacity-60 transition group-hover:text-ele-green group-hover:opacity-100">Edit</span>
      </div>
      {children}
    </div>
  );
}

// Stage 1, Details: what the job is, for whom, where, plus the site survey. The four edit
// cards + the survey findings live together because they are all "capture the job" work.
function DetailsTab({
  project,
  reload,
  isAdmin,
  openClientEditor,
  onClientEditorOpened,
}: {
  project: ProjectDetail;
  reload: () => Promise<void>;
  isAdmin: boolean;
  openClientEditor?: boolean;
  onClientEditorOpened?: () => void;
}) {
  const [editing, setEditing] = useState<OverviewCard | null>(null);

  // Session AAX Loop 2: the "Add client details" next-step CTA opens the client edit modal
  // directly, so the user lands typing rather than hunting for the right card.
  useEffect(() => {
    if (openClientEditor) {
      setEditing("client");
      onClientEditorOpened?.();
    }
  }, [openClientEditor, onClientEditorOpened]);
  const close = () => setEditing(null);
  const saved = async () => {
    setEditing(null);
    await reload();
  };
  const address = project.siteAddress ? `${project.siteAddress}${project.sitePostcode ? `, ${project.sitePostcode}` : ""}` : "-";
  return (
    <div>
      <p className="mb-3 flex items-center justify-between gap-2 text-sm text-ele-body">
        <span>The job, the client and the site. Click any card to edit it.</span>
      </p>
      <EleReservationLine projectId={project.id} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <EditableCard title="Scope" onEdit={() => setEditing("scope")}>
          <Field label="Title">{project.title}</Field>
          <Field label="Lifts in scope">{project.liftDetails ? <CollapsibleText text={project.liftDetails} /> : "-"}</Field>
          <Field label="Scope of works">{project.scopeSummary ? <ScopeSummaryView text={project.scopeSummary} /> : "-"}</Field>
          <Field label="Exclusions">{project.exclusions ? <CollapsibleText text={project.exclusions} /> : "-"}</Field>
          <Field label="Applicable standard">{project.applicableStandard ?? "-"}</Field>
          <Field label="Notes">{project.notes ? <CollapsibleText text={project.notes} /> : "-"}</Field>
        </EditableCard>
        <EditableCard title="Client and site" onEdit={() => setEditing("client")}>
          <Field label="Customer">{project.customerName ?? "-"}</Field>
          <Field label="Contact name">{project.clientName ?? "-"}</Field>
          <Field label="Contact email">{project.clientEmail ?? "-"}</Field>
          <Field label="Contact phone">{project.clientPhone ?? "-"}</Field>
          <Field label="Site">{project.siteName ?? "-"}</Field>
          <Field label="Address">{address}</Field>
        </EditableCard>
        {/* Commercials: the editable inputs (Project value + Works classification) are click-to-edit;
            the calculated figures (Total cost + Margin) are read-only and derived from the Costing tab.
            The two cards share one grid cell so the Overview stays a two-column grid (Session AV, Item 4). */}
        <div className="space-y-4">
          <EditableCard title="Commercials" onEdit={() => setEditing("commercials")}>
            <Field label="Project value (ex VAT, calculated on the Costing tab)">{gbp(project.contractValue)}</Field>
            <Field label="Type of work">{project.worksClassification ? WORKS_TYPE_LABELS[project.worksClassification] : "Not set"}</Field>
          </EditableCard>
          {isAdmin && project.costSummary ? (
            <div className="space-y-3 rounded-ele border border-ele-purple-accent p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg text-ele-strong">Cost and margin</h3>
                <span className="font-menu text-[11px] uppercase tracking-wide text-ele-body opacity-60">Read only</span>
              </div>
              <Field label="Total cost to ELE (calculated from Costing tab)">{gbp(project.costSummary.totalCostToEle)}</Field>
              <Field label="Margin (auto-calculated)">
                {project.costSummary.margin == null ? (
                  <span className="text-ele-body">Set the project value on the Costing tab.</span>
                ) : project.costSummary.margin < 0 ? (
                  <StatusPill word={`Costs exceed project value (${gbp(project.costSummary.margin)})`} tone="amber" />
                ) : (
                  <span className="text-ele-green">
                    {gbp(project.costSummary.margin)}{project.costSummary.marginPct != null ? ` (${project.costSummary.marginPct}%)` : ""}
                  </span>
                )}
              </Field>
            </div>
          ) : null}
        </div>
        {/* Session AAA Item 4: these are the CLIENT'S obligations (Building Safety Act 2022 /
            Landlord and Tenant Act 1985), recorded so ELE can advise the client and never
            mobilise before the client has met them. Advisory labels; the BSR/S20 LOGIC is
            unchanged (previewHrb / checkBsrGateway / the banners). */}
        <EditableCard title="Building details (advisory)" onEdit={() => setEditing("building")}>
          <p className="text-xs text-ele-body opacity-80">{BUILDING_ADVISORY_NOTE}</p>
          <Field label="Height (m) / storeys">
            {project.buildingHeightM ?? "-"}m / {project.buildingStoreys ?? "-"}
          </Field>
          <Field label="Number of units (residential)">
            {project.residentialUnits ?? "-"} <span className="text-[10px] text-ele-body opacity-70">(relevant to Section 20)</span>
          </Field>
          <Field label="Higher-risk building (18m or 7+ storeys)?">{project.bsrRequired ? (project.bsrCleared ? `Yes: Gateway 2 recorded ${formatDate(project.bsrGateway2Date)}` : "Yes: approval outstanding") : "No"}</Field>
          <Field label="Section 20 consultation required by client?">{project.section20Required ? `Yes${project.section20Stage ? ` (stage: ${project.section20Stage.replace(/_/g, " ")})` : ""}` : "No"}</Field>
        </EditableCard>
      </div>
      <ProjectSurveySection projectId={project.id} onProjectChanged={reload} />
      <SurveySection project={project} reload={reload} />
      {editing === "scope" ? <ScopeEditModal project={project} onClose={close} onSaved={saved} /> : null}
      {editing === "client" ? <ClientSiteEditModal project={project} onClose={close} onSaved={saved} /> : null}
      {editing === "commercials" ? <CommercialsEditModal project={project} onClose={close} onSaved={saved} /> : null}
      {editing === "building" ? <BuildingEditModal project={project} onClose={close} onSaved={saved} /> : null}
    </div>
  );
}

// Today's calendar date as an ISO string (YYYY-MM-DD), for seeding the quotation date input.
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// A fresh, empty quotation detail (the fast path where a project has never had a breakdown built).
const EMPTY_QUOTATION_DETAIL: QuotationDetail = {
  clientRef: null,
  quoteRef: null,
  quotationDate: null,
  liftsInScope: null,
  lineItems: [],
  existingInstall: [],
  supplier: null,
  additionalTerms: [],
};

// The quotation panel (the Quote tab's step 3). The PDF is generated server-side from the project data (the
// approved scope, the project value from the Costing tab, the survey condition notes). The office
// confirms the client reference (their PO), and can OVERRIDE the quote reference and the issue date
// (Session AS, Item 1): the quote ref defaults to the auto project ref and the date to today, and
// both persist so the sent PDF shows the office's values. From here the office previews the PDF and
// emails or sends it via Signable.
function QuotationPanel({ project, reload }: { project: ProjectDetail; reload: () => Promise<void> }) {
  const [clientRef, setClientRef] = useState(project.quotationDetail?.clientRef ?? "");
  // Seeded from the stored override, else the sensible default (the auto project ref / today).
  const [quoteRef, setQuoteRef] = useState(project.quotationDetail?.quoteRef ?? project.projectRef);
  const [quotationDate, setQuotationDate] = useState(project.quotationDetail?.quotationDate ?? todayIso());
  const [busy, setBusy] = useState<"preview" | "email" | "signable" | "resend" | "ref" | "accept" | null>(null);
  // Session AEI: the optional second signatory for the acceptance link, pre-filled from the project.
  const [showSecond, setShowSecond] = useState(project.secondSignatory != null);
  const [secondName, setSecondName] = useState(project.secondSignatory?.name ?? "");
  const [secondEmail, setSecondEmail] = useState(project.secondSignatory?.email ?? "");
  const [secondRole, setSecondRole] = useState(project.secondSignatory?.role ?? "");
  // Send feedback (Session AAC, Item 3): success is green and names the recipient; an
  // already-sent is amber guidance; an error is red and explicit. Never silent.
  const [msg, setMsg] = useState<{ text: string; kind: "success" | "info" | "error" } | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  const signed = project.quotationSignedAt != null;

  useEffect(() => {
    setClientRef(project.quotationDetail?.clientRef ?? "");
    setQuoteRef(project.quotationDetail?.quoteRef ?? project.projectRef);
    setQuotationDate(project.quotationDetail?.quotationDate ?? todayIso());
  }, [project.quotationDetail?.clientRef, project.quotationDetail?.quoteRef, project.quotationDetail?.quotationDate, project.projectRef]);

  // Session AEI: keep the second-signatory form in step with the stored value.
  useEffect(() => {
    setShowSecond(project.secondSignatory != null);
    setSecondName(project.secondSignatory?.name ?? "");
    setSecondEmail(project.secondSignatory?.email ?? "");
    setSecondRole(project.secondSignatory?.role ?? "");
  }, [project.secondSignatory]);

  // Persist the quotation header fields (client ref, the quote-ref override, the issue date), only
  // when one actually changed, preserving any existing quotation breakdown. The server leaves the
  // project value untouched when there are no line items, so saving these on a fast-path quotation
  // never disturbs the value set on the Costing tab. NEVER writes once the quotation is signed (that
  // record is fixed), so re-previewing a signed quotation does not mutate its breakdown. The quote
  // ref stores null when left at (or cleared to) the auto project ref, and the date stores null when
  // cleared, so both revert to their sensible defaults (the project ref / today).
  async function persistQuotationHeader(): Promise<void> {
    if (signed) return;
    const storedClientRef = project.quotationDetail?.clientRef ?? null;
    const storedQuoteRef = project.quotationDetail?.quoteRef ?? null;
    const storedDate = project.quotationDetail?.quotationDate ?? null;
    const nextClientRef = clientRef.trim() || null;
    const nextQuoteRef = quoteRef.trim() && quoteRef.trim() !== project.projectRef ? quoteRef.trim() : null;
    const nextDate = quotationDate.trim() || null;
    if (nextClientRef === storedClientRef && nextQuoteRef === storedQuoteRef && nextDate === storedDate) return;
    const detail = project.quotationDetail ?? EMPTY_QUOTATION_DETAIL;
    await api.projects.saveQuotation(project.id, { ...detail, clientRef: nextClientRef, quoteRef: nextQuoteRef, quotationDate: nextDate });
  }

  async function preview() {
    // Open the tab SYNCHRONOUSLY (in the click gesture) so the browser does not block the popup
    // after the awaited save; then point it at the PDF once the client ref is persisted.
    const win = window.open("", "_blank");
    setBusy("preview");
    setMsg(null);
    try {
      await persistQuotationHeader();
      const href = api.projects.quotationPdfHref(project.id);
      if (win) win.location.href = href;
      else window.open(href, "_blank", "noopener,noreferrer"); // popup was blocked; best-effort retry
      await reload();
    } catch (e) {
      if (win) win.close();
      setMsg({ text: e instanceof ApiError ? e.message || e.code : "Could not generate the PDF.", kind: "error" });
    } finally {
      setBusy(null);
    }
  }

  async function send(via: "email" | "signable") {
    // The server resolves the recipient (the project's contact email, else the customer's
    // billing email). With neither, the send is blocked HERE with a clear inline error --
    // it never silently goes anywhere else.
    const recipient = project.quotationRecipient;
    if (!recipient) {
      setMsg({ text: "No client email on the project. Add a contact email on the Client and site card first.", kind: "error" });
      return;
    }
    // The confirm names the actual recipient, so a wrong address is caught BEFORE the send.
    if (
      via === "signable" &&
      !(await confirm({
        title: "Send for signature?",
        message: `Send this quotation to ${recipient} for e-signature? Signing it forms a binding agreement.`,
        confirmLabel: "Send for signature",
      }))
    )
      return;
    // The emailed quotation is information only, never the acceptance route (locked Signable
    // rule). Session ABN: the email carries the online quotation LINK, not a PDF attachment.
    if (
      via === "email" &&
      !(await confirm({
        title: "Email the quotation?",
        message: `Email the quotation to ${recipient} for information only? To send it for formal acceptance, use Send via Signable.`,
        confirmLabel: "Email quotation",
      }))
    )
      return;
    setBusy(via);
    setMsg(null);
    try {
      await persistQuotationHeader();
      const res = await api.projects.sendQuotation(project.id, via);
      if (res.result === "sent") {
        setMsg(
          via === "email"
            ? { text: `Quotation emailed to ${res.to ?? recipient} (information only).`, kind: "success" }
            : { text: `Quotation sent via Signable to ${res.to ?? recipient} for signature.`, kind: "success" },
        );
      } else if (res.result === "already_sent") {
        setMsg({
          text: `Already sent via Signable${res.sentAt ? ` on ${formatDate(res.sentAt)}` : ""}. The client can still sign the original envelope. Use Re-send via Signable below if they need a fresh link.`,
          kind: "info",
        });
      } else {
        setMsg({ text: `The send finished with an unexpected status (${res.result}).`, kind: "error" });
      }
      await reload();
    } catch (e) {
      setMsg({ text: e instanceof ApiError ? e.message || e.code : "Could not send the quotation.", kind: "error" });
    } finally {
      setBusy(null);
    }
  }

  // Session AEI (D1): send the customer the branded ACCEPTANCE LINK. They open the ELE acceptance
  // page, read the quotation AND the full Conditions of Contract, then Sign and Accept (which
  // triggers the Signable envelope). An optional second signatory is saved first.
  async function sendAcceptanceLink() {
    const recipient = project.quotationRecipient;
    if (!recipient) {
      setMsg({ text: "No client email on the project. Add a contact email on the Client and site card first.", kind: "error" });
      return;
    }
    // The toggle reflects the desired state: on with valid fields saves the second signatory, off
    // clears any previously-saved one. An incomplete second signatory blocks with a clear message.
    let second: { name: string; email: string; role: string } | null;
    if (showSecond) {
      if (!secondName.trim() || !secondEmail.trim()) {
        setMsg({ text: "Enter the second signatory's name and email, or turn off the second signatory.", kind: "error" });
        return;
      }
      second = { name: secondName.trim(), email: secondEmail.trim(), role: secondRole.trim() };
    } else {
      second = null;
    }
    const secondNote = second ? ` ${second.name} will also be asked to sign.` : "";
    if (
      !(await confirm({
        title: "Send acceptance link?",
        message: `Send the acceptance link to ${recipient}?${secondNote} They will read the quotation and the Conditions of Contract, then sign to accept.`,
        confirmLabel: "Send the link",
      }))
    )
      return;
    setBusy("accept");
    setMsg(null);
    try {
      await persistQuotationHeader();
      const res = await api.projects.sendAcceptanceLink(project.id, second);
      if (res.result === "sent") {
        setMsg({ text: `Acceptance link sent to ${res.to ?? recipient}.`, kind: "success" });
      } else {
        setMsg({ text: `The send finished with an unexpected status (${res.result}).`, kind: "error" });
      }
      await reload();
    } catch (e) {
      setMsg({ text: e instanceof ApiError ? e.message || e.code : "Could not send the acceptance link.", kind: "error" });
    } finally {
      setBusy(null);
    }
  }

  // Session AAC, Item 5: re-send = void the outstanding envelope, then a fresh send. Only
  // offered while an envelope is outstanding (the button replaces Send via Signable).
  async function resend() {
    const recipient = project.quotationRecipient;
    if (!recipient) {
      setMsg({ text: "No client email on the project. Add a contact email on the Client and site card first.", kind: "error" });
      return;
    }
    if (
      !(await confirm({
        title: "Re-send via Signable?",
        message: `Cancel the original signing link and send a fresh Signable envelope to ${recipient}? The old link will stop working.`,
        confirmLabel: "Re-send",
      }))
    )
      return;
    setBusy("resend");
    setMsg(null);
    try {
      await persistQuotationHeader();
      const res = await api.projects.resendQuotation(project.id);
      // Only claim the old link was cancelled when the void actually succeeded; a failed
      // void is reported honestly (the client may still hold a working original link).
      setMsg({
        text:
          `A fresh signing link was sent via Signable to ${res.to ?? recipient}. ` +
          (res.oldLinkCancelled === false
            ? "The original link could not be cancelled and may still work; if the client signs the OLD link, contact the office."
            : "The original link was cancelled."),
        kind: "success",
      });
      await reload();
    } catch (e) {
      setMsg({ text: e instanceof ApiError ? e.message || e.code : "Could not re-send the quotation.", kind: "error" });
    } finally {
      setBusy(null);
    }
  }

  async function doDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await api.projects.deleteQuotation(project.id);
      setShowDelete(false);
      // The server voids a live Signable envelope best-effort; when that failed, the old
      // signing link may still work at Signable, so the office is told rather than the
      // failure living only in the audit log (Session AED).
      if (res.quotationEnvelopeVoidFailed) {
        setMsg({
          text: "The quotation was deleted, but the old Signable signing link could not be cancelled. Ask the client to disregard the earlier signing email; a late signature can no longer activate this project.",
          kind: "error",
        });
      }
      await reload();
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete the quotation.");
    } finally {
      setDeleting(false);
    }
  }

  const lineCount = project.quotationDetail?.lineItems.length ?? 0;
  const canDelete = !project.quotationSignedAt && (lineCount > 0 || project.hasQuotation || project.quotationDetail != null);
  // Session AAC, Item 5: the three envelope states drive the panel. Once an envelope is
  // outstanding the primary send button is REPLACED by a clear status plus a re-send action
  // (voids the old link, sends a fresh one), so a second click can never look like it would
  // create a duplicate envelope. ("Viewed" is not shown: the platform only receives the
  // signed-envelope-complete webhook, so sent and signed are the states it truthfully knows.)
  // quotationOutstanding is the server's REAL-envelope flag: a crash-orphaned send sentinel
  // keeps the normal Send button (whose claim self-heals), never a dead-end Re-send.
  const envelopeOutstanding = project.quotationOutstanding;
  // Session ABB, Loop 3 (one obvious action per screen): while the quotation is still being
  // assembled (no approved scope or no value yet), the earlier step's button is the screen's one
  // green primary, so Send via Signable waits as a quiet ghost. The moment the quotation is
  // ready, it becomes the solid green primary. Same action, same guards, re-weighted only.
  const readyToSend = Boolean(project.scopeSummary?.trim()) && project.contractValue != null;
  return (
    <div data-focus-section="quote-send" className="space-y-3 rounded-ele border border-ele-blue/40 bg-ele-surface p-4">
      {confirmDialog}
      <h3 className="font-heading text-lg text-ele-strong">3. Send the quotation</h3>
      <p className="text-sm text-ele-body">
        {project.quotationSignedAt ? (
          <span className="font-bold text-ele-green">
            Signed via Signable on {formatDate(project.quotationSignedAt)}.
            {project.status === "accepted" ? " The project moved to Accepted automatically." : ""}
          </span>
        ) : envelopeOutstanding ? (
          <span>
            <span className="font-bold text-amber-300">Sent via Signable{project.quotationSentAt ? ` on ${formatDate(project.quotationSentAt)}` : ""}. Awaiting the client's signature.</span>{" "}
            Signable reminds the client automatically every 3 days. Only one envelope can be open at a time; use Re-send to cancel the original link and issue a fresh one.
          </span>
        ) : project.contractValue != null ? (
          "Ready to send. The PDF is generated from the project scope, value and survey notes."
        ) : (
          "Set the project value on the Costing tab and approve the scope above before sending."
        )}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><label className={labelCls} htmlFor="q-ref">Quote reference</label><input id="q-ref" className={inputCls} value={quoteRef} disabled={signed} onChange={(e) => setQuoteRef(e.target.value)} placeholder={project.projectRef} /></div>
        <div><label className={labelCls} htmlFor="q-clientref">Client reference (optional)</label><input id="q-clientref" className={inputCls} value={clientRef} disabled={signed} onChange={(e) => setClientRef(e.target.value)} placeholder="Their PO number" /></div>
        <div><label className={labelCls} htmlFor="q-date">Date</label><input id="q-date" type="date" className={inputCls} value={quotationDate} disabled={signed} onChange={(e) => setQuotationDate(e.target.value)} /></div>
      </div>
      {/* Session AEI (D1): the acceptance-link flow is the PRIMARY send-for-signing path. The
          customer opens the branded ELE acceptance page, reads the quotation AND the full
          Conditions of Contract, then Sign and Accept (which triggers the Signable envelope).
          Send via Signable below stays as the direct-envelope alternative (skips the page). */}
      {!project.quotationSignedAt ? (
        <div className="space-y-2 rounded-ele border border-ele-green/40 bg-ele-surface p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-heading text-sm text-ele-strong">Send for acceptance</p>
              <p className="text-xs text-ele-body">Primary signatory: {project.quotationRecipient ?? "no client email yet"}</p>
            </div>
            {project.acceptanceLinkSent ? (
              <span className="rounded-full bg-ele-blue/15 px-2.5 py-0.5 text-xs font-bold text-ele-blue">
                Link sent{project.quotationSentAt ? ` ${formatDate(project.quotationSentAt)}` : ""}
              </span>
            ) : null}
          </div>
          <label className="flex items-center gap-2 text-xs text-ele-body">
            <input type="checkbox" checked={showSecond} disabled={busy !== null} onChange={(e) => setShowSecond(e.target.checked)} />
            Add a second signatory (e.g. a managing agent or building owner who must also sign)
          </label>
          {showSecond ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input className={inputCls} placeholder="Full name" value={secondName} disabled={busy !== null} onChange={(e) => setSecondName(e.target.value)} aria-label="Second signatory name" />
              <input className={inputCls} type="email" placeholder="Email" value={secondEmail} disabled={busy !== null} onChange={(e) => setSecondEmail(e.target.value)} aria-label="Second signatory email" />
              <input className={inputCls} placeholder="Role (optional)" value={secondRole} disabled={busy !== null} onChange={(e) => setSecondRole(e.target.value)} aria-label="Second signatory role" />
            </div>
          ) : null}
          <button
            onClick={sendAcceptanceLink}
            disabled={busy !== null || !project.quotationRecipient}
            className={
              readyToSend
                ? "rounded-ele bg-ele-green px-4 py-1.5 font-menu text-xs font-bold tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
                : `${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`
            }
          >
            {busy === "accept" ? "Sending..." : project.acceptanceLinkSent ? "Resend acceptance link" : "Send acceptance link"}
          </button>
          {!readyToSend ? <p className="text-xs text-amber-300">Set the project value on the Costing tab and approve the scope above before sending.</p> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {!project.quotationSignedAt && !envelopeOutstanding ? (
          <button
            onClick={() => send("signable")}
            disabled={busy !== null || !project.quotationRecipient}
            className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}
            title="Send the Signable envelope directly, skipping the acceptance page"
          >
            {busy === "signable" ? "Sending..." : "Send via Signable"}
          </button>
        ) : null}
        {envelopeOutstanding ? (
          <button onClick={resend} disabled={busy !== null || !project.quotationRecipient} className={`${btnSm} border-amber-500 text-amber-400 hover:bg-amber-500/10`}>{busy === "resend" ? "Re-sending..." : "Re-send via Signable (cancels the original link)"}</button>
        ) : null}
        <button onClick={preview} disabled={busy !== null} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>{busy === "preview" ? "Opening..." : "Generate quotation PDF"}</button>
        {/* Once SIGNED there is nothing to email: the plain PDF's locked footer invites the
            client to accept via the secure link, which is wrong after signing (the signed
            record is filed automatically). The server backstops this with 409 already_signed. */}
        {!project.quotationSignedAt ? (
          <button onClick={() => send("email")} disabled={busy !== null || !project.quotationRecipient} className={`${btnSm} border-ele-blue text-ele-blue hover:bg-ele-blue/10`}>{busy === "email" ? "Emailing..." : "Email quotation (information only)"}</button>
        ) : null}
        {/* The standard project works T&Cs, downloaded as a PDF. No project data: the same
            document every time, styled to match "Generate quotation PDF" (secondary). */}
        <a href={api.projects.tcPdfHref(project.id)} target="_blank" rel="noopener noreferrer" className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>Download T&amp;Cs</a>
        {canDelete ? (
          // Session AAX Loop 4: destructive, so it lives in the overflow behind the confirm.
          <OverflowMenu label="Quotation actions">
            <button role="menuitem" onClick={() => { setDeleteError(null); setShowDelete(true); }} disabled={busy !== null} className={overflowDangerItemCls}>
              Delete quotation
            </button>
          </OverflowMenu>
        ) : null}
      </div>
      {!project.quotationSignedAt ? (
        <p className="text-xs text-ele-body opacity-80">To send for formal acceptance, use Send acceptance link: the client reads the quotation and the full Conditions of Contract, then signs to accept. Send via Signable sends the signing envelope directly. The emailed quotation is for the client's information only.</p>
      ) : null}
      {!project.quotationRecipient ? (
        <p className="text-xs text-amber-300">No client email on the project. Add a contact email on the Client and site card to enable sending.</p>
      ) : null}
      {msg ? (
        <p
          role={msg.kind === "error" ? "alert" : "status"}
          className={`text-sm ${msg.kind === "error" ? "text-ele-danger" : msg.kind === "success" ? "text-ele-green" : "text-amber-300"}`}
        >
          {msg.text}
        </p>
      ) : null}
      <ConfirmDeleteModal
        open={showDelete}
        title="Delete this quotation?"
        message="This clears the quotation so you can re-quote. The project and its costs are kept."
        warning={project.hasQuotation ? "This quotation has been sent for signature. Deleting it voids the signing link." : null}
        confirmLabel="Delete quotation"
        variant="destructive"
        busy={deleting}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => { if (!deleting) setShowDelete(false); }}
      />
    </div>
  );
}

// The selectable classification values for the edit modals (the Director's five, in order).
// A legacy project still classified with the retired 'modification' value gets that value
// appended as a display-only option, so the select shows its real current state.
function worksClassChoices(current: WorksClassification | null): WorksClassification[] {
  return current === "modification" ? [...WORKS_TYPE_OPTIONS, "modification"] : WORKS_TYPE_OPTIONS;
}

// String -> number coercers shared by the card modals. `null` clears the field.
const numOrNull = (s: string): number | null => {
  const x = parseFloat(s);
  return s.trim() !== "" && Number.isFinite(x) ? x : null;
};
const intOrNull = (s: string): number | null => {
  const x = Math.round(parseFloat(s));
  return s.trim() !== "" && Number.isFinite(x) ? x : null;
};

// A focused, self-contained edit modal shell for one Overview card (Session AU, Item 1). No giant
// single modal: each card owns its own fields.
function CardModal({ title, busy, err, onClose, onSave, wide, children }: {
  title: string;
  busy: boolean;
  err: string | null;
  onClose: () => void;
  onSave: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()} width={wide ? "max-w-[560px]" : "max-w-lg"} margin="my-8" panelClassName="p-6">
        <h2 className="mb-4 font-heading text-2xl tracking-wide">{title}</h2>
        <div className="space-y-3">{children}</div>
        {err ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={onSave} disabled={busy} className={MODAL_ACTION_BTN}>{busy ? "Saving..." : "Save"}</Button>
        </div>
    </ModalShell>
  );
}

function ScopeEditModal({ project, onClose, onSaved }: { project: ProjectDetail; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    title: project.title,
    worksClassification: (project.worksClassification ?? "") as WorksClassification | "",
    applicableStandard: project.applicableStandard ?? "",
    liftDetails: project.liftDetails ?? "",
    scopeSummary: project.scopeSummary ?? "",
    exclusions: project.exclusions ?? "",
    notes: project.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  // Pre-fill the applicable standard from the classification when it is still blank (the office can
  // override it). Works classification is shared with the Commercials card; both write the same field.
  function changeClass(v: WorksClassification | "") {
    setForm((f) => ({ ...f, worksClassification: v, applicableStandard: f.applicableStandard.trim() === "" ? standardForWorksClass(v) : f.applicableStandard }));
  }
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.projects.update(project.id, {
        title: form.title.trim() || undefined,
        // Only send the classification when it changed: a legacy 'modification' value is
        // display-only (no longer settable), so re-sending it unchanged must not 400.
        worksClassification:
          form.worksClassification === (project.worksClassification ?? "") ? undefined : form.worksClassification || null,
        applicableStandard: form.applicableStandard,
        liftDetails: form.liftDetails,
        scopeSummary: form.scopeSummary,
        exclusions: form.exclusions,
        notes: form.notes,
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not save.");
      setBusy(false);
    }
  }
  return (
    <CardModal title="Edit scope" busy={busy} err={err} onClose={onClose} onSave={save} wide>
      <div><label className={labelCls} htmlFor="sc-title">Title</label><input id="sc-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="sc-class">What type of work is this?</label>
          <select id="sc-class" className={inputCls} value={form.worksClassification} onChange={(e) => changeClass(e.target.value as WorksClassification | "")}>
            <option value="">Not set</option>
            {worksClassChoices(project.worksClassification).map((c) => <option key={c} value={c}>{WORKS_TYPE_LABELS[c]}</option>)}
          </select>
        </div>
        <div><label className={labelCls} htmlFor="sc-std">Applicable standard</label><input id="sc-std" className={inputCls} value={form.applicableStandard} onChange={(e) => set("applicableStandard", e.target.value)} /></div>
      </div>
      <div><label className={labelCls} htmlFor="sc-lifts">Lifts in scope</label><textarea id="sc-lifts" className={inputCls} rows={2} value={form.liftDetails} onChange={(e) => set("liftDetails", e.target.value)} /></div>
      <div><label className={labelCls} htmlFor="sc-scope">Scope of works</label><textarea id="sc-scope" className={inputCls} rows={3} value={form.scopeSummary} onChange={(e) => set("scopeSummary", e.target.value)} /></div>
      <div><label className={labelCls} htmlFor="sc-excl">Exclusions</label><textarea id="sc-excl" className={inputCls} rows={2} value={form.exclusions} onChange={(e) => set("exclusions", e.target.value)} /></div>
      <div><label className={labelCls} htmlFor="sc-notes">Notes</label><textarea id="sc-notes" className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
    </CardModal>
  );
}

// Link (or unlink) an existing Customer + Site record, and edit the contact. Picking a customer
// pre-fills the contact fields (editable); picking a site fills the site fields and shows the site's
// address read-only. A pre-contract enquiry with no linked customer keeps free-text fields.
function ClientSiteEditModal({ project, onClose, onSaved }: { project: ProjectDetail; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    customerId: project.customerId ?? "",
    clientName: project.clientName ?? "",
    clientEmail: project.clientEmail ?? "",
    clientPhone: project.clientPhone ?? "",
    siteId: project.siteId ?? "",
    siteName: project.siteName ?? "",
    siteAddress: project.siteAddress ?? "",
    sitePostcode: project.sitePostcode ?? "",
  });
  // Session SC2A: the customer is a typeahead picker, so the register is never loaded to fill it.
  const [customerPick, setCustomerPick] = useState<Customer | null>(null);
  const [siteList, setSiteList] = useState<Site[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!form.customerId) {
      setSiteList([]);
      return;
    }
    void sites.list({ customerId: form.customerId }).then(setSiteList).catch(() => {});
  }, [form.customerId]);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function pickCustomer(id: string, rec: PickerRecord | null) {
    const c = (rec?.raw as unknown as Customer | undefined) ?? customerPick ?? undefined;
    setCustomerPick(rec ? (rec.raw as unknown as Customer) : null);
    setForm((f) => ({
      ...f,
      customerId: id,
      clientName: id ? (c?.billingContact ?? f.clientName) : f.clientName,
      clientEmail: id ? (c?.billingEmail ?? f.clientEmail) : f.clientEmail,
      clientPhone: id ? (c?.phone ?? f.clientPhone) : f.clientPhone,
      // Clear the site link (it may belong to a different customer); the office re-picks.
      siteId: "",
      siteName: id ? "" : f.siteName,
      siteAddress: id ? "" : f.siteAddress,
      sitePostcode: id ? "" : f.sitePostcode,
    }));
  }
  function pickSite(id: string) {
    const s = siteList.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      siteId: id,
      siteName: id ? (s?.name ?? f.siteName) : f.siteName,
      siteAddress: id ? (s?.address ?? "") : f.siteAddress,
      sitePostcode: id ? (s?.postcode ?? "") : f.sitePostcode,
    }));
  }
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.projects.update(project.id, {
        customerId: form.customerId || null,
        clientName: form.clientName.trim() || null,
        clientEmail: form.clientEmail.trim() || null,
        clientPhone: form.clientPhone.trim() || null,
        siteId: form.siteId || null,
        siteName: form.siteName.trim() || null,
        siteAddress: form.siteAddress.trim() || null,
        sitePostcode: form.sitePostcode.trim() || null,
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not save.");
      setBusy(false);
    }
  }
  const siteLinked = form.siteId !== "";
  return (
    <CardModal title="Edit client and site" busy={busy} err={err} onClose={onClose} onSave={save} wide>
      <div>
        <RecordPicker
          kind="customer"
          id="cs-customer"
          label="Customer"
          value={form.customerId}
          selectedLabel={form.customerId && form.customerId === project.customerId ? project.customerName : undefined}
          placeholder="Not linked (search to link a customer)"
          onChange={(id, rec) => pickCustomer(id, rec)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><label className={labelCls} htmlFor="cs-cname">Contact name</label><input id="cs-cname" className={inputCls} value={form.clientName} onChange={(e) => set("clientName", e.target.value)} /></div>
        <div><label className={labelCls} htmlFor="cs-cemail">Contact email</label><input id="cs-cemail" type="email" className={inputCls} value={form.clientEmail} onChange={(e) => set("clientEmail", e.target.value)} /></div>
        <div><label className={labelCls} htmlFor="cs-cphone">Contact phone</label><input id="cs-cphone" className={inputCls} value={form.clientPhone} onChange={(e) => set("clientPhone", e.target.value)} /></div>
      </div>
      {form.customerId ? (
        <div>
          <label className={labelCls} htmlFor="cs-site">Site</label>
          <select id="cs-site" className={inputCls} value={form.siteId} onChange={(e) => pickSite(e.target.value)}>
            <option value="">Not linked (enter details below)</option>
            {siteList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      ) : null}
      {siteLinked ? (
        <div>
          {/* A read-only display, not a form control: use a plain heading, not a <label> (which would
              have no control to associate with). */}
          <div className={labelCls}>Site address</div>
          <p className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
            {form.siteAddress || "No address on record"}{form.sitePostcode ? `, ${form.sitePostcode}` : ""}
          </p>
          <p className="mt-1 text-[11px] text-ele-body opacity-70">The address is pulled from the linked site record.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><label className={labelCls} htmlFor="cs-sname">Site name</label><input id="cs-sname" className={inputCls} value={form.siteName} onChange={(e) => set("siteName", e.target.value)} /></div>
          <div><label className={labelCls} htmlFor="cs-spost">Postcode</label>
            <PostcodeLookup
              id="cs-spost"
              postcode={form.sitePostcode}
              onPostcodeChange={(v) => set("sitePostcode", v)}
              onSelect={(r) => setForm((f) => ({ ...f, sitePostcode: r.postcode || f.sitePostcode, siteAddress: r.address }))}
              className={inputCls}
            />
          </div>
          <div className="sm:col-span-2"><label className={labelCls} htmlFor="cs-saddr">Site address</label><input id="cs-saddr" className={inputCls} value={form.siteAddress} onChange={(e) => set("siteAddress", e.target.value)} /></div>
        </div>
      )}
    </CardModal>
  );
}

function CommercialsEditModal({ project, onClose, onSaved }: { project: ProjectDetail; onClose: () => void; onSaved: () => Promise<void> }) {
  // Session AAA Item 3: the project value is CALCULATED (cost + markup on the Costing tab, or the
  // quotation line-item sum under the TOTALS RULE), so this modal shows it read-only and edits
  // only the works classification.
  const quotationOwnsValue = (project.quotationDetail?.lineItems.length ?? 0) > 0;
  const [form, setForm] = useState({
    worksClassification: (project.worksClassification ?? "") as WorksClassification | "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.projects.update(project.id, {
        // Only send the classification when it changed (a legacy 'modification' value is
        // display-only and no longer settable).
        worksClassification:
          form.worksClassification === (project.worksClassification ?? "") ? undefined : form.worksClassification || null,
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not save.");
      setBusy(false);
    }
  }
  return (
    <CardModal title="Edit commercials" busy={busy} err={err} onClose={onClose} onSave={save}>
      <div>
        <div className={labelCls}>Project value (ex VAT)</div>
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">{gbp(project.contractValue)}</p>
        <p className="mt-1 text-[11px] text-ele-body opacity-70">
          {quotationOwnsValue
            ? "The value is set by the quotation line items (their sum). Edit the quotation on the Quote tab."
            : "The value is calculated from the cost lines and markups on the Costing tab."}
        </p>
      </div>
      <div>
        <label className={labelCls} htmlFor="cm-class">What type of work is this?</label>
        <select id="cm-class" className={inputCls} value={form.worksClassification} onChange={(e) => setForm((f) => ({ ...f, worksClassification: e.target.value as WorksClassification | "" }))}>
          <option value="">Not set</option>
          {worksClassChoices(project.worksClassification).map((c) => <option key={c} value={c}>{WORKS_TYPE_LABELS[c]}</option>)}
        </select>
      </div>
    </CardModal>
  );
}

function BuildingEditModal({ project, onClose, onSaved }: { project: ProjectDetail; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    buildingHeightM: project.buildingHeightM != null ? String(project.buildingHeightM) : "",
    buildingStoreys: project.buildingStoreys != null ? String(project.buildingStoreys) : "",
    residentialUnits: project.residentialUnits != null ? String(project.residentialUnits) : "",
    bsrRequired: project.bsrRequired,
    section20Required: project.section20Required,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  const hrbHint = previewHrb({ buildingHeightM: form.buildingHeightM, buildingStoreys: form.buildingStoreys, residentialUnits: form.residentialUnits });
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.projects.update(project.id, {
        buildingHeightM: numOrNull(form.buildingHeightM),
        buildingStoreys: intOrNull(form.buildingStoreys),
        residentialUnits: intOrNull(form.residentialUnits),
        // Explicit toggles: the office's yes/no wins over the dimension-derived default.
        bsrRequired: form.bsrRequired,
        section20Required: form.section20Required,
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not save.");
      setBusy(false);
    }
  }
  return (
    <CardModal title="Edit building details (advisory)" busy={busy} err={err} onClose={onClose} onSave={save}>
      <p className="text-xs text-ele-body opacity-80">{BUILDING_ADVISORY_NOTE}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><label className={labelCls} htmlFor="bd-height">Height (m)</label><input id="bd-height" type="number" className={inputCls} value={form.buildingHeightM} onChange={(e) => set("buildingHeightM", e.target.value)} /></div>
        <div><label className={labelCls} htmlFor="bd-storeys">Storeys</label><input id="bd-storeys" type="number" className={inputCls} value={form.buildingStoreys} onChange={(e) => set("buildingStoreys", e.target.value)} /></div>
        <div>
          <label className={labelCls} htmlFor="bd-units">Number of units (residential) <span className="text-[10px] normal-case opacity-70">(relevant to Section 20)</span></label>
          <input id="bd-units" type="number" className={inputCls} value={form.residentialUnits} onChange={(e) => set("residentialUnits", e.target.value)} />
        </div>
      </div>
      {hrbHint && !form.bsrRequired ? (
        <p className="rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300">These dimensions indicate a Higher-Risk Building. Consider marking BSR as required.</p>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-ele-strong">
        <input type="checkbox" checked={form.bsrRequired} onChange={(e) => set("bsrRequired", e.target.checked)} />
        Higher-risk building (18m or 7+ storeys)?
      </label>
      <label className="flex items-center gap-2 text-sm text-ele-strong">
        <input type="checkbox" checked={form.section20Required} onChange={(e) => set("section20Required", e.target.checked)} />
        Section 20 consultation required by client?
      </label>
    </CardModal>
  );
}

const DOC_TYPE_LABELS: Record<ProjectDocType, string> = {
  scope_of_works: "Scope of works",
  quotation_cover_letter: "Quotation covering letter",
  subcontractor_instruction: "Subcontractor instruction",
  completion_report: "Completion report",
  quotation_pdf: "Quotation PDF",
  supplier_quote: "Supplier quote",
  other: "Other",
};

// The Delivery tab's AI-draftable types. The scope of works and the covering letter belong to
// the QUOTE stage and have their own dedicated panels there (Session AAB), so they are not in
// this list.
const AI_DOC_OPTIONS: { value: ProjectDocType; label: string }[] = [
  { value: "subcontractor_instruction", label: "Subcontractor instruction" },
  { value: "completion_report", label: "Completion report" },
];

// Session AS, Item 2: the scope of works and the covering letter are unique per project (the
// server enforces it); their dedicated Quote-tab panels confirm before regenerating over an
// existing draft (ReplaceDraftConfirm).

// ---- Quote tab (stage 3): the three-action quotation flow --------------------
// Step 1 generate + approve the scope, step 2 an optional covering letter, step 3 send. From a
// costed project: Generate scope -> Approve -> Send via Signable. No tab hunting, no separate
// "use in quotation" ceremony (the approve button explains itself).
function QuoteTab({
  project,
  reload,
  autoGenerateScope,
  onAutoGenerateHandled,
}: {
  project: ProjectDetail;
  reload: () => Promise<void>;
  autoGenerateScope?: boolean;
  onAutoGenerateHandled?: () => void;
}) {
  const projectId = project.id;
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setDocs(await api.projects.documents(projectId));
    } catch {
      setLoadError(true);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => {
    void load();
  }, [load]);

  // The docs list is newest-first, so find() picks the CURRENT scope/letter. Any older
  // duplicates (legacy pre-Session-AS data, or a manual add) are surfaced below the steps so
  // no document is ever stranded unreachable.
  const scopeDocs = docs.filter((d) => d.docType === "scope_of_works");
  const coverDocs = docs.filter((d) => d.docType === "quotation_cover_letter");
  const scopeDoc = scopeDocs[0] ?? null;
  const coverDoc = coverDocs[0] ?? null;
  const duplicateDocs = [...scopeDocs.slice(1), ...coverDocs.slice(1)];

  async function removeDuplicate(docId: string) {
    try {
      await api.projects.removeDocument(projectId, docId);
    } catch {
      /* the reload below re-renders whatever the server holds */
    }
    await load();
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ele-body">
        Three steps: generate and approve the scope of works, add an optional covering letter, then send the quotation
        for signature. Signable is the only acceptance route.
      </p>
      {loading ? (
        <Loader shape="inline" />
      ) : loadError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">Could not load the documents. Please try again in a moment.</p>
      ) : (
        <>
          <ScopeStep
            project={project}
            doc={scopeDoc}
            reloadDocs={load}
            reloadProject={reload}
            autoGenerate={autoGenerateScope}
            onAutoGenerateHandled={onAutoGenerateHandled}
          />
          <CoverLetterStep project={project} doc={coverDoc} reloadDocs={load} reloadProject={reload} />
          {duplicateDocs.length > 0 ? (
            <div className="rounded-ele border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-sm text-amber-300">Older duplicate drafts (the steps above use the newest). Delete the ones you no longer need.</p>
              <div className="mt-2 space-y-1">
                {duplicateDocs.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-ele-body">
                    <span>{d.title} <span className="text-xs opacity-70">({DOC_TYPE_LABELS[d.docType]})</span></span>
                    <ConfirmButton onConfirm={() => removeDuplicate(d.id)} label="Delete" className={buttonClasses("destructive", "sm")} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
      <QuotationPanel project={project} reload={reload} />
      <ProjectQuotationVersionsPanel projectId={project.id} projectStatus={project.status} reload={reload} />
    </div>
  );
}

// Session ATI (Q-8): the version/option ledger. D26 is not decided by the Director (the
// decisions board carries it open) -- this renders option (a), the default for an unattended
// session: one current quotation record edited in place, with a version history the office can
// open (the customer never sees it; the public page only ever shows the current version). "Start
// a new version" supersedes whatever is current and clears the Quote tab above for a fresh build,
// which the office then sends through the existing Send flow exactly as for the first quotation.
function ProjectQuotationVersionsPanel({ projectId, projectStatus, reload }: { projectId: string; projectStatus: ProjectStatus; reload: () => Promise<void> }) {
  const [versions, setVersions] = useState<ProjectQuotationVersion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [label, setLabel] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const [deleteVersionError, setDeleteVersionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.projects.quotationVersions(projectId);
      setVersions(res.versions);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message || e.code : "Could not load the quotation versions.");
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function startVersion() {
    setStarting(true);
    setStartError(null);
    try {
      await api.projects.startQuotationVersion(projectId, label.trim() || null);
      setShowStart(false);
      setLabel("");
      await load();
      await reload();
    } catch (e) {
      setStartError(e instanceof ApiError ? e.message || e.code : "Could not start a new version.");
    } finally {
      setStarting(false);
    }
  }

  // RULE 1: delete a superseded, unsigned version/option. The server refuses (409) the current
  // version or a signed one; only a real (non-synthetic) row can be deleted at all.
  async function deleteVersion(versionId: string) {
    setDeleteBusyId(versionId);
    setDeleteVersionError(null);
    try {
      await api.projects.deleteQuotationVersion(projectId, versionId);
      await load();
    } catch (e) {
      setDeleteVersionError(e instanceof ApiError ? e.message || e.code : "Could not delete this version.");
    } finally {
      setDeleteBusyId(null);
    }
  }

  if (!versions || versions.length === 0) return null;

  const current = versions.find((v) => v.isCurrent);
  const history = versions.filter((v) => !v.isCurrent);

  return (
    <div className="space-y-3 rounded-ele border border-ele-purple-accent/40 bg-ele-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg text-ele-strong">Quotation versions</h3>
        {!["accepted", "works_scheduled", "in_progress", "on_hold", "complete", "invoiced"].includes(projectStatus) ? (
          <button onClick={() => { setStartError(null); setLabel(""); setShowStart(true); }} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>
            Start a new version / option
          </button>
        ) : null}
      </div>
      {loadError ? <p role="alert" className="text-sm text-ele-danger">{loadError}</p> : null}
      {current ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-ele-body">
          <StatusPill word={current.statusLabel} tone={statusEntry("quote", current.status).tone} />
          <span>{current.label ? current.label : "Current quotation"}</span>
          {current.valuePounds != null ? <span className="opacity-80">{gbp(current.valuePounds)}</span> : null}
          {current.sentAt ? <span className="opacity-70">Sent {formatDate(current.sentAt)}</span> : null}
          {current.openedAt ? <span className="opacity-70">Opened {formatDate(current.openedAt)}</span> : null}
          {current.expiresAt ? <span className="opacity-70">Valid until {formatDate(current.expiresAt)}</span> : null}
          {current.lostReasonLabel ? (
            <span className="opacity-70">Lost reason: {current.lostReasonLabel}{current.lostReasonNote ? `: ${current.lostReasonNote}` : ""}</span>
          ) : null}
        </div>
      ) : null}
      {history.length > 0 ? (
        <div>
          <button onClick={() => setExpanded((v) => !v)} className="font-menu text-xs uppercase tracking-wide text-ele-body underline-offset-2 hover:underline">
            {expanded ? "Hide" : "Show"} {history.length} earlier version{history.length === 1 ? "" : "s"}
          </button>
          {expanded ? (
            <ul className="mt-2 space-y-1.5">
              {history.map((v) => {
                const canDeleteVersion = !v.signedAt && !v.id.startsWith("live:");
                return (
                  <li key={v.id} className="flex flex-wrap items-center gap-2 text-xs text-ele-body opacity-90">
                    <StatusPill word={v.statusLabel} tone={statusEntry("quote", v.status).tone} />
                    <span>{v.label ? v.label : "Untitled version"}</span>
                    {v.valuePounds != null ? <span>{gbp(v.valuePounds)}</span> : null}
                    {v.sentAt ? <span className="opacity-70">Sent {formatDate(v.sentAt)}</span> : null}
                    {v.signedAt ? <span className="opacity-70">Signed {formatDate(v.signedAt)}</span> : null}
                    {v.lostReasonLabel ? <span className="opacity-70">Lost: {v.lostReasonLabel}{v.lostReasonNote ? `: ${v.lostReasonNote}` : ""}</span> : null}
                    {canDeleteVersion ? (
                      <ConfirmButton
                        onConfirm={() => deleteVersion(v.id)}
                        label="Delete"
                        busy={deleteBusyId === v.id}
                        className="rounded-ele border border-ele-danger px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-danger hover:bg-ele-danger/10 disabled:opacity-50"
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {deleteVersionError ? <p role="alert" className="mt-1 text-xs text-ele-danger">{deleteVersionError}</p> : null}
        </div>
      ) : null}
      {showStart ? (
        <ModalShell ariaLabel="Start a new quotation version" onClose={() => !starting && setShowStart(false)} width="max-w-md" border="border-ele-purple-accent" panelClassName="p-6 text-left">
          <h2 className="font-heading text-xl uppercase tracking-wide text-ele-strong">Start a new version / option</h2>
          <p className="mt-2 text-sm text-ele-body">
            The current quotation is kept as history. The Quote tab above clears so you can build the new one from scratch,
            then send it exactly as before.
          </p>
          <div className="mt-3">
            <label className={labelCls} htmlFor="version-label">Label (optional)</label>
            <input id="version-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Revision 2, or Option A, steel doors" />
          </div>
          {startError ? <p role="alert" className="mt-2 text-sm text-ele-danger">{startError}</p> : null}
          <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
            <button onClick={() => { if (!starting) setShowStart(false); }} disabled={starting} className={`${btnSm} border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
              Cancel
            </button>
            <Button onClick={startVersion} disabled={starting} className={MODAL_ACTION_BTN} variant="secondary" size="sm">
              {starting ? "Starting..." : "Start new version"}
            </Button>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}

// The shared replace-confirm dialog for regenerating a unique-per-project draft (Session AS, Item 2).
function ReplaceDraftConfirm({ label, onCancel, onConfirm }: { label: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <ModalShell ariaLabel="Replace document" onClose={onCancel} width="max-w-md" border="border-amber-500" panelClassName="p-6">
        <h2 className="font-heading text-xl uppercase tracking-wide text-amber-300">Replace the existing {label}?</h2>
        <p className="mt-2 text-sm text-ele-body">
          A {label} already exists for this project. Generating a new AI draft overwrites its content and resets it to
          draft. This cannot be undone.
        </p>
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onCancel} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <button onClick={onConfirm} className={`${btnSm} border-amber-500 text-amber-300 hover:bg-amber-500/10 ${MODAL_ACTION_BTN}`}>Replace with new draft</button>
        </div>
    </ModalShell>
  );
}

// Step 1: the scope of works. One canonical generate button (the Survey duplicate was removed);
// the draft is reviewed/refined inline and approved via "Approve scope and use in quotation".
// The scope counts as IN PLACE when project.scopeSummary is set (that is the exact text the
// quotation PDF prints).
function ScopeStep({
  project,
  doc,
  reloadDocs,
  reloadProject,
  autoGenerate,
  onAutoGenerateHandled,
}: {
  project: ProjectDetail;
  doc: ProjectDocument | null;
  reloadDocs: () => Promise<void>;
  reloadProject: () => Promise<void>;
  autoGenerate?: boolean;
  onAutoGenerateHandled?: () => void;
}) {
  const approved = Boolean(project.scopeSummary?.trim());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgError, setMsgError] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  // The editor shows automatically while a draft needs review; once approved it collapses
  // behind a View-or-edit toggle.
  const [openEditor, setOpenEditor] = useState(false);
  // Session ABB, Loop 2: the write-by-hand fallback lives HERE, inline, seeded from the survey
  // notes -- no Details-tab round trip, and the condition content is never typed twice.
  const [manualText, setManualText] = useState<string | null>(null);
  const [manualBusy, setManualBusy] = useState(false);

  async function generate() {
    setConfirmReplace(false);
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.generateDocument(project.id, { docType: "scope_of_works" });
      await reloadDocs();
      setOpenEditor(true);
      setMsg("Scope draft ready. Review it below, refine it if needed, then approve it.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not generate the scope draft.");
    } finally {
      setBusy(false);
    }
  }

  // The "Generate scope" next-step CTA does what it says: it triggers the draft on arrival.
  // Only when no draft exists (never a silent regenerate over one), and the flag is consumed
  // either way so a re-render cannot fire twice. The ref guards StrictMode's dev double-invoke
  // of mount effects, which would otherwise fire the AI call twice; it re-arms once the parent
  // clears the flag so a later CTA press still works.
  const autoGenFired = useRef(false);
  useEffect(() => {
    if (!autoGenerate) {
      autoGenFired.current = false;
      return;
    }
    if (autoGenFired.current) return;
    autoGenFired.current = true;
    onAutoGenerateHandled?.();
    if (!doc && !busy) void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume-once trigger
  }, [autoGenerate]);

  // Save the hand-written scope straight onto the project (that field is the exact text the
  // quotation PDF prints), which also counts as approved.
  async function saveManual() {
    if (manualText == null || !manualText.trim()) return;
    setManualBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.update(project.id, { scopeSummary: manualText });
      await reloadProject();
      setManualText(null);
      setMsg("Scope saved. This exact text will print in the quotation PDF.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not save the scope.");
    } finally {
      setManualBusy(false);
    }
  }

  const showEditor = doc != null && (openEditor || !approved);
  return (
    <div data-focus-section="quote-scope" className="rounded-ele border border-ele-purple-accent p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg text-ele-strong">1. Scope of works</h3>
        {approved ? <StatusPill word="Approved" tone="green" /> : doc ? <StatusPill word="Draft in review" tone="amber" /> : <StatusPill word="Not started" tone="grey" />}
      </div>
      <p className="mt-1 text-sm text-ele-body">
        Drafted in one click from the project details, the survey notes and any attached supplier quotes. The approved
        scope is the exact text the quotation PDF prints.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {doc ? (
          <>
            <button onClick={() => setConfirmReplace(true)} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>{busy ? "Drafting..." : "Regenerate draft"}</button>
            {approved && !showEditor ? (
              <button onClick={() => setOpenEditor(true)} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>View or edit</button>
            ) : null}
          </>
        ) : (
          <>
            <Button size="sm" variant={approved || manualText != null ? "secondary" : "primary"} onClick={generate} disabled={busy}>
              {busy ? "Drafting..." : "Generate scope draft"}
            </Button>
            {/* The inline write-by-hand fallback (Session ABB, Loop 2): seeded from the survey
                notes so the condition content is never typed twice, and no Details round trip. */}
            {manualText == null ? (
              <button
                onClick={() => setManualText(project.scopeSummary?.trim() ? project.scopeSummary! : (project.conditionNotes ?? ""))}
                disabled={busy}
                className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}
              >
                Write it by hand
              </button>
            ) : null}
          </>
        )}
        {msg ? <span role={msgError ? "alert" : "status"} className={`text-xs ${msgError ? "text-ele-danger" : "text-amber-300"}`}>{msg}</span> : null}
      </div>
      {manualText != null && !doc ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-ele-body opacity-80">
            {!project.scopeSummary?.trim() && project.conditionNotes?.trim()
              ? "Pre-filled from your survey notes so nothing is typed twice. Edit it into the scope you want, then save."
              : "Write the scope of works, then save."}
          </p>
          <textarea
            aria-label="Scope of works (written by hand)"
            rows={8}
            className={inputCls}
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            disabled={manualBusy}
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveManual} disabled={manualBusy || !manualText.trim()} size="sm">
              {manualBusy ? "Saving..." : "Save scope"}
            </Button>
            <button onClick={() => setManualText(null)} disabled={manualBusy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {approved && !doc ? (
        <p className="mt-2 text-xs text-ele-body opacity-70">The scope was entered by hand. Generating a draft here replaces nothing until you approve it.</p>
      ) : null}
      {/* Keyed on the document's content VERSION: a regenerate updates the doc in place (same id),
          and without the remount the editor would keep showing the pre-regeneration text, which
          Approve would then write into the quotation scope (the AAB review's F1). */}
      {showEditor ? <DocumentEditor key={`${doc!.id}:${doc!.updatedAt}`} projectId={project.id} doc={doc!} reload={reloadDocs} reloadProject={reloadProject} /> : null}
      {confirmReplace ? <ReplaceDraftConfirm label="scope of works" onCancel={() => setConfirmReplace(false)} onConfirm={() => void generate()} /> : null}
    </div>
  );
}

// Step 2: the optional covering letter. Collapsed by default (progressive disclosure): the
// simple case never needs it. The letter becomes the body of the information-only email; the
// price, the attachment note and the sign-off are appended automatically at send time.
function CoverLetterStep({ project, doc, reloadDocs, reloadProject }: { project: ProjectDetail; doc: ProjectDocument | null; reloadDocs: () => Promise<void>; reloadProject: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgError, setMsgError] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);

  async function generate() {
    setConfirmReplace(false);
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.generateDocument(project.id, { docType: "quotation_cover_letter" });
      await reloadDocs();
      setOpenEditor(true);
      setMsg("Covering letter drafted. Review it before sending.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not draft the covering letter.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg text-ele-strong">2. Covering letter (optional)</h3>
        {doc ? <StatusPill word="Drafted" tone="green" /> : <StatusPill word="None" tone="grey" />}
      </div>
      <p className="mt-1 text-sm text-ele-body">
        A short letter that becomes the body of the information-only email. The quoted price, the attachment note and
        the sign-off are added automatically, so the letter never states them.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          onClick={() => (doc ? setConfirmReplace(true) : void generate())}
          disabled={busy}
          className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}
        >
          {busy ? "Drafting..." : doc ? "Regenerate letter" : "Generate covering letter"}
        </button>
        {doc ? (
          <button onClick={() => setOpenEditor(!openEditor)} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>
            {openEditor ? "Hide" : "View or edit"}
          </button>
        ) : null}
        {msg ? <span role={msgError ? "alert" : "status"} className={`text-xs ${msgError ? "text-ele-danger" : "text-amber-300"}`}>{msg}</span> : null}
      </div>
      {/* Keyed on the content version so a regenerate remounts the editor with the new text (F1). */}
      {doc && openEditor ? <DocumentEditor key={`${doc.id}:${doc.updatedAt}`} projectId={project.id} doc={doc} reload={reloadDocs} reloadProject={reloadProject} /> : null}
      {confirmReplace ? <ReplaceDraftConfirm label="quotation covering letter" onCancel={() => setConfirmReplace(false)} onConfirm={() => void generate()} /> : null}
    </div>
  );
}

// ---- Delivery tab (stage 4) ---------------------------------------------------
// Everything after acceptance: subcontractor lifecycle + agreements, stage invoicing, the
// programme, and the delivery/closeout documents (works instructions, completion reports).
function DeliveryTab({ project, reload, isAdmin }: { project: ProjectDetail; reload: () => Promise<void>; isAdmin: boolean }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-ele-body">
        Everything after acceptance: subcontractor agreements and status, stage invoicing, the programme, and the
        delivery documents.
      </p>
      <SubcontractorsSection project={project} reload={reload} isAdmin={isAdmin} />
      <CostsInvoicing project={project} />
      <TimelineCard project={project} />
      {/* Session BDM: the works as ONE job over several working days (never billed from the job, nothing sent). */}
      <ProjectWorksScheduler projectId={project.id} status={project.status} />
      <PostWorksLolerOfferCard project={project} reload={reload} isAdmin={isAdmin} />
      <DeliveryDocuments project={project} reloadProject={reload} />
    </div>
  );
}

// Session ABS: the post-works LOLER examination offer. After modernisation or major repair works
// a LOLER thorough examination is required by statute, carried out by an independent competent
// person (ELE holds no LOLER accreditation and never examines). The office can offer to ARRANGE it
// via ELE's inspection partner as a separate paid service. The card shows only once the works are
// in progress or complete; the send is admin-only (the endpoint gates it). Each send is stamped +
// audited server-side; the card shows when the offer was last sent and stays available to re-send.
function PostWorksLolerOfferCard({ project, reload, isAdmin }: { project: ProjectDetail; reload: () => Promise<void>; isAdmin: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(project.lolerOfferSentAt);

  // Only relevant once the works are under way or finished.
  if (project.status !== "in_progress" && project.status !== "complete") return null;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.projects.sendLolerOffer(project.id);
      setSentAt(res.sentAt);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not send the offer. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <h3 className="mb-2 font-heading text-lg text-ele-strong">Post-works LOLER examination</h3>
      <p className="text-sm text-ele-body">
        A LOLER thorough examination is required after modernisation or major repair works. This must be carried out by an
        independent competent person. East London Elevators can arrange this with ELTI UK on your behalf as an additional
        service at a separate cost.
      </p>
      {sentAt ? <p className="mt-3 text-sm text-ele-green">Offer sent {formatDate(sentAt)}</p> : null}
      {error ? <p className="mt-3 text-sm text-ele-danger">{error}</p> : null}
      {isAdmin ? (
        <Button onClick={() => void send()} disabled={busy} className="mt-3" size="sm">
          {busy ? "Sending..." : sentAt ? "Re-send offer to customer" : "Send offer to customer"}
        </Button>
      ) : (
        <p className="mt-3 text-sm text-ele-body">An administrator can send this offer to the customer.</p>
      )}
    </div>
  );
}

// The delivery-stage document store: works instructions, completion reports, filed quotation
// PDFs and any manually added documents. The scope of works and the covering letter live on the
// Quote tab (their own panels); supplier quote PDFs live on the Costing tab (cost inputs).
function DeliveryDocuments({ project, reloadProject }: { project: ProjectDetail; reloadProject: () => Promise<void> }) {
  const projectId = project.id;
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [genType, setGenType] = useState<ProjectDocType>("subcontractor_instruction");
  const [genSub, setGenSub] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [genMsg, setGenMsg] = useState<string | null>(null);
  const [genError, setGenError] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [docActionError, setDocActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setDocs(await api.projects.documents(projectId));
    } catch {
      setLoadError(true);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => {
    void load();
  }, [load]);

  async function runGenerate() {
    setGenBusy(true);
    setGenMsg(null);
    setGenError(false);
    try {
      const created = await api.projects.generateDocument(projectId, {
        docType: genType,
        subcontractorId: genType === "subcontractor_instruction" ? genSub || undefined : undefined,
      });
      await load();
      setOpen(created.id); // expand the new draft for review
      setGenMsg("AI draft created. Review and edit it before sending.");
    } catch (e) {
      setGenError(true);
      setGenMsg(e instanceof ApiError ? e.message || e.code : "Could not draft the document.");
    } finally {
      setGenBusy(false);
    }
  }

  // Delete a document straight from the collapsed card (Session AS, Item 3). The expanded editor
  // keeps its own Delete; both call the same endpoint. Collapse the row if it was open, then reload.
  async function removeDoc(docId: string) {
    setDeletingId(docId);
    setDocActionError(null);
    try {
      await api.projects.removeDocument(projectId, docId);
      if (open === docId) setOpen(null);
      await load();
    } catch (e) {
      setDocActionError(e instanceof ApiError ? e.message || e.code : "Could not delete the document.");
    } finally {
      setDeletingId(null);
    }
  }

  // Quote-stage documents have their own panels on the Quote tab; supplier quotes live on Costing.
  const visibleDocs = docs.filter((d) => d.docType !== "supplier_quote" && d.docType !== "scope_of_works" && d.docType !== "quotation_cover_letter");

  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg text-ele-strong">Documents</h3>
          <p className="text-sm text-ele-body">Works instructions to subcontractors, completion reports and anything added by hand. AI drafts are saved here for review; nothing is sent automatically.</p>
        </div>
        <button onClick={() => setAdding(true)} className={`${btnSm} shrink-0 border-ele-purple-accent text-ele-body hover:text-ele-green`}>Add manually</button>
      </div>

      <div className="mb-4 rounded-ele border border-ele-blue/40 bg-ele-surface p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56">
            <label className={labelCls} htmlFor="doc-gen-type">Generate with AI</label>
            <select id="doc-gen-type" className={inputCls} value={genType} onChange={(e) => setGenType(e.target.value as ProjectDocType)}>
              {AI_DOC_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {genType === "subcontractor_instruction" ? (
            <div className="min-w-56">
              <label className={labelCls} htmlFor="doc-gen-sub">Subcontractor</label>
              <select id="doc-gen-sub" className={inputCls} value={genSub} onChange={(e) => setGenSub(e.target.value)}>
                <option value="">Choose</option>
                {project.subcontractors.map((s) => <option key={s.id} value={s.id}>{s.company}</option>)}
              </select>
            </div>
          ) : null}
          <Button
            onClick={() => void runGenerate()}
            disabled={genBusy || (genType === "subcontractor_instruction" && !genSub)}
            size="sm"
          >
            {genBusy ? "Drafting..." : "Generate draft"}
          </Button>
        </div>
        {genMsg ? (
          <p role={genError ? "alert" : undefined} className={`mt-2 text-sm ${genError ? "text-ele-danger" : "text-amber-300"}`}>{genMsg}</p>
        ) : null}
      </div>
      {docActionError ? <p role="alert" className="mb-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{docActionError}</p> : null}
      {loading ? (
        <Loader shape="inline" />
      ) : loadError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">Could not load the documents. Please try again in a moment.</p>
      ) : visibleDocs.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">No delivery documents yet.</p>
      ) : (
        <div className="space-y-2">
          {visibleDocs.map((d) => (
            <div key={d.id} className="rounded-ele border border-ele-purple-accent">
              {/* The toggle (title + badges + indicator) and the Delete are siblings, never nested
                  buttons, so deleting from the collapsed row does not also expand it (Session AS, Item 3). */}
              <div className="flex w-full items-center gap-2 px-4 py-3">
                <button onClick={() => setOpen(open === d.id ? null : d.id)} aria-expanded={open === d.id} className="flex flex-1 items-center justify-between gap-2 text-left">
                  <span>
                    <span className="font-heading text-ele-strong">{d.title}</span>
                    <span className="ml-2 rounded-full bg-ele-inset px-2 py-0.5 text-[10px] uppercase text-ele-body">{DOC_TYPE_LABELS[d.docType]}</span>
                    {d.generatedByAi ? <span className="ml-1 rounded-full bg-ele-blue/15 px-2 py-0.5 text-[10px] uppercase text-ele-blue">AI draft</span> : null}
                    <span className="ml-1 rounded-full bg-ele-inset px-2 py-0.5 text-[10px] uppercase text-ele-body">{d.status === "final" ? "Approved" : d.status}</span>
                  </span>
                  <span className="text-ele-body" aria-hidden>{open === d.id ? "-" : "+"}</span>
                </button>
                <OverflowMenu label={`Actions for ${d.title}`}>
                  <ConfirmButton
                    onConfirm={() => removeDoc(d.id)}
                    label="Delete"
                    busy={deletingId === d.id}
                    title="Delete this document"
                    className={overflowDangerItemCls}
                  />
                </OverflowMenu>
              </div>
              {open === d.id ? <DocumentEditor projectId={projectId} doc={d} reload={load} reloadProject={reloadProject} /> : null}
            </div>
          ))}
        </div>
      )}
      {adding ? <AddDocumentModal projectId={projectId} onClose={() => setAdding(false)} onAdded={async () => { setAdding(false); await load(); }} /> : null}
    </div>
  );
}

function DocumentEditor({ projectId, doc, reload, reloadProject }: { projectId: string; doc: ProjectDocument; reload: () => Promise<void>; reloadProject: () => Promise<void> }) {
  const [content, setContent] = useState(doc.content ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgError, setMsgError] = useState(false);
  const [showSend, setShowSend] = useState(false);
  // Session AW, Item 2: inline AI refinement of this draft (shown on AI drafts only).
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  // A rendered-only quotation PDF is sent from the Quotation panel, not as an email body.
  // A rendered quotation PDF and an uploaded supplier quote are not emailed as a document body.
  const canSend = doc.docType !== "quotation_pdf" && doc.docType !== "supplier_quote";
  // A works instruction is drafted in Gmail (RC2), so the platform cannot see it go: the office records the day it was sent.
  const isWorksInstruction = doc.docType === "subcontractor_instruction";
  const [markingSent, setMarkingSent] = useState(false);
  const [sentOn, setSentOn] = useState(() => londonToday());

  async function save() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.updateDocument(projectId, doc.id, { content });
      await reload();
      setMsg("Saved.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not save the document.");
    } finally {
      setBusy(false);
    }
  }
  async function setStatus(status: DocStatus) {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.updateDocument(projectId, doc.id, { status });
      await reload();
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not update the status.");
    } finally {
      setBusy(false);
    }
  }
  // Ask the AI to apply one specific change to this draft. Save the current text first, so the AI
  // refines exactly what is on screen (any manual edits included), then apply the change; the result
  // replaces the text area and the input clears. Nothing is sent; the document returns to draft.
  async function refine() {
    const instruction = refineInstruction.trim();
    if (!instruction) return;
    setRefineBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.updateDocument(projectId, doc.id, { content });
      const updated = await api.projects.refineDocument(projectId, doc.id, instruction);
      setContent(updated.content ?? "");
      setRefineInstruction("");
      await reload();
      setMsg("Refined. Review the change before sending.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not refine the document.");
    } finally {
      setRefineBusy(false);
    }
  }
  // Mark this scope draft as the active scope for the quotation PDF: save its (possibly edited)
  // content into the project's scope summary (which the PDF renders) and approve the document.
  async function useInQuotation() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.updateDocument(projectId, doc.id, { content, status: "final" });
      await api.projects.update(projectId, { scopeSummary: content });
      await reload();
      await reloadProject();
      setMsg("Scope approved. This exact text will print in the quotation PDF.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not set the scope.");
    } finally {
      setBusy(false);
    }
  }
  async function markSent() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      const r = await api.projects.markDocumentSent(projectId, doc.id, sentOn);
      setMarkingSent(false);
      setMsg(`Recorded as sent to ${r.to} on ${r.sentOnLabel}.`);
      await reload();
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not record it as sent.");
    } finally {
      setBusy(false);
    }
  }
  async function clearSent() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.clearDocumentSent(projectId, doc.id);
      setMsg("The sent record is cleared.");
      await reload();
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not clear the sent record.");
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.removeDocument(projectId, doc.id);
      await reload();
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not delete the document.");
      setBusy(false);
    }
  }
  return (
    <div className="border-t border-ele-purple-accent p-4">
      {doc.driveUrl ? (
        <a href={doc.driveUrl} target="_blank" rel="noopener noreferrer" className="mb-2 inline-block text-sm text-ele-green hover:underline">Open the filed PDF</a>
      ) : null}
      {doc.sentAt ? (
        <p role="status" className="mb-1 rounded-ele border border-ele-green/40 bg-ele-surface px-3 py-1.5 text-xs text-ele-green">Sent{doc.sentTo ? ` to ${doc.sentTo}` : ""} on {formatDate(doc.sentAt)}.</p>
      ) : (
        <p className="mb-1 rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300">Review before sending. Nothing is sent automatically.</p>
      )}
      <textarea className={inputCls + " font-mono"} rows={12} value={content} onChange={(e) => setContent(e.target.value)} disabled={refineBusy || busy} />
      {doc.generatedByAi ? (
        <div className="mt-2 rounded-ele border border-ele-blue/40 bg-ele-surface p-3">
          <label className={labelCls} htmlFor={`refine-${doc.id}`}>Refine with AI</label>
          <div className="flex flex-wrap items-end gap-2">
            <input
              id={`refine-${doc.id}`}
              type="text"
              className={inputCls + " min-w-56 flex-1"}
              placeholder="Describe one change, e.g. Remove the scaffolding line"
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value)}
              disabled={refineBusy || busy}
            />
            <button
              onClick={refine}
              disabled={refineBusy || busy || !refineInstruction.trim()}
              className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}
            >
              {refineBusy ? "Refining..." : "Refine"}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-ele-body opacity-70">The AI applies only the change you describe and keeps the rest of the document as it is. It is saved as a new draft for review.</p>
        </div>
      ) : null}
      {msg ? <p role={msgError ? "alert" : "status"} className={`mt-2 rounded-ele px-3 py-2 text-sm ${msgError ? "border border-ele-danger bg-red-500/10 text-ele-danger" : "text-ele-green"}`}>{msg}</p> : null}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-ele-body">
          <span className="font-menu uppercase tracking-wide">Status</span>
          <select className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm outline-none focus:border-ele-green" value={doc.status} disabled={busy} onChange={(e) => setStatus(e.target.value as DocStatus)}>
            <option value="draft">Draft</option>
            <option value="final">Approved</option>
            {doc.status === "sent" ? <option value="sent">Sent</option> : null}
            {doc.status === "signed" ? <option value="signed">Signed</option> : null}
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {/* One green primary per section (Loop 4): the scope's is Approve; any other
              sendable document's is Send. Everything else is a quiet ghost or lives in
              the overflow behind the two-step confirm. */}
          {doc.docType === "scope_of_works" ? (
            <Button onClick={useInQuotation} disabled={busy} size="sm" title="This exact text prints in the quotation PDF">
              Approve scope and use in quotation
            </Button>
          ) : null}
          <button onClick={save} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>{busy ? "Saving..." : "Save"}</button>
          {canSend ? (
            doc.docType === "scope_of_works" ? (
              <button onClick={() => setShowSend(true)} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>{doc.sentAt ? "Send again" : "Send"}</button>
            ) : (
              <Button size="sm" onClick={() => setShowSend(true)} disabled={busy}>{doc.sentAt ? "Send again" : "Send"}</Button>
            )
          ) : null}
          <OverflowMenu label="Document actions">
            {isWorksInstruction && !doc.sentAt ? (
              <button type="button" onClick={() => { setSentOn(londonToday()); setMarkingSent(true); }} disabled={busy} className={overflowItemCls}>Mark as sent</button>
            ) : null}
            {isWorksInstruction && doc.sentAt ? (
              <ConfirmButton onConfirm={clearSent} label="Clear sent record" busy={busy} className={overflowItemCls} />
            ) : null}
            <ConfirmButton onConfirm={remove} label="Delete" busy={busy} className={overflowDangerItemCls} />
          </OverflowMenu>
        </div>
      </div>
      {doc.docType === "scope_of_works" ? (
        <p className="mt-1 text-[11px] text-ele-body opacity-70">Approving makes this exact text the scope the quotation PDF prints.</p>
      ) : null}
      {markingSent ? (
        <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
          <label className={labelCls} htmlFor={`sent-on-${doc.id}`}>Day you sent it from Gmail</label>
          <div className="flex flex-wrap items-end gap-2">
            <input id={`sent-on-${doc.id}`} type="date" className={inputCls + " w-44"} value={sentOn} max={londonToday()} onChange={(e) => setSentOn(e.target.value)} disabled={busy} />
            <ConfirmButton onConfirm={markSent} label="Record as sent" busy={busy} disabled={!sentOn} />
            <button type="button" onClick={() => setMarkingSent(false)} disabled={busy} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>Cancel</button>
          </div>
          <p className="mt-1 text-[11px] text-ele-body opacity-70">This records that you sent the works instruction yourself. Nothing is sent from here.</p>
        </div>
      ) : null}
      {showSend ? (
        <SendDocumentModal
          projectId={projectId}
          doc={doc}
          onClose={() => setShowSend(false)}
          onSent={async (r) => {
            setShowSend(false);
            // Session RC2: a works instruction to a subcontractor is a Gmail draft, never sent from here.
            if (r.drafted) {
              setMsgError(false);
              setMsg(`A draft to ${r.to} is waiting in Gmail Drafts for you to send. Once you have sent it, use Mark as sent in the document's actions.`);
            }
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

// The Send confirmation modal (Session AU, Item 2). Loads the resolved recipient + subject from the
// server (so the office sees exactly where it will go), shows the first 200 characters of the body,
// and sends only on explicit confirmation. Nothing is sent automatically.
function SendDocumentModal({ projectId, doc, onClose, onSent }: { projectId: string; doc: ProjectDocument; onClose: () => void; onSent: (r: { to: string; drafted?: boolean }) => Promise<void> }) {
  const [preview, setPreview] = useState<DocumentSendPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.projects
      .documentSendPreview(projectId, doc.id)
      .then((p) => {
        if (live) setPreview(p);
      })
      .catch((e) => {
        if (live) setError(e instanceof ApiError ? e.message || e.code : "Could not prepare the send.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [projectId, doc.id]);

  async function confirmSend() {
    setSending(true);
    setError(null);
    try {
      const r = await api.projects.sendDocument(projectId, doc.id);
      await onSent(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not send the document.");
      setSending(false);
    }
  }

  const rawBody = (doc.content ?? "").trim();
  const bodyPreview = rawBody.slice(0, 200);
  return (
    <ModalShell ariaLabel="Send document" onClose={() => !sending && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
        <h2 className="mb-4 font-heading text-2xl tracking-wide">Send document</h2>
        {loading ? (
          <p className="text-sm text-ele-body">Preparing...</p>
        ) : preview && !preview.canSend ? (
          <p role="alert" className="rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">{preview.reason}</p>
        ) : preview ? (
          <div className="space-y-3 text-sm">
            <div><span className="mr-2 font-menu text-[11px] uppercase tracking-wide text-ele-body">To</span><span className="text-ele-strong">{preview.to}</span></div>
            <div><span className="mr-2 font-menu text-[11px] uppercase tracking-wide text-ele-body">Subject</span><span className="text-ele-strong">{preview.subject}</span></div>
            <div>
              <span className="mb-1 block font-menu text-[11px] uppercase tracking-wide text-ele-body">Preview</span>
              <p className="whitespace-pre-wrap rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-ele-body">{bodyPreview || "(No content)"}{rawBody.length > 200 ? "..." : ""}</p>
            </div>
            <p className="text-xs text-ele-body opacity-70">
              {preview.recipientKind === "subcontractor"
                ? "This is left as a Gmail draft for you to read and send from Gmail. Nothing is sent from here."
                : "Nothing is sent until you confirm."}
            </p>
          </div>
        ) : null}
        {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{error}</p> : null}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} disabled={sending} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={confirmSend} disabled={sending || loading || !preview?.canSend} className={MODAL_ACTION_BTN}>{preview?.recipientKind === "subcontractor" ? (sending ? "Drafting..." : "Draft in Gmail") : sending ? "Sending..." : "Confirm send"}</Button>
        </div>
    </ModalShell>
  );
}

// The Delivery tab's manual "Add document" type list: the delivery/closeout types only. The
// scope of works and the covering letter are authored via their Quote-tab step panels (a manual
// add of either here would be filtered from this list and silently vanish, and a duplicate
// scope would shadow the real one); a supplier quote is upload-only (the Costing-tab attach).
const DOC_TYPES: ProjectDocType[] = ["subcontractor_instruction", "completion_report", "other"];

function AddDocumentModal({ projectId, onClose, onAdded }: { projectId: string; onClose: () => void; onAdded: () => Promise<void> }) {
  const [docType, setDocType] = useState<ProjectDocType>(DOC_TYPES[0]!);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function add() {
    if (!title.trim()) {
      setErr("Enter a title.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.projects.createDocument(projectId, { docType, title: title.trim(), content: content || undefined });
      await onAdded();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not add the document."));
      setBusy(false);
    }
  }
  return (
    <ModalShell ariaLabel="Add document" onClose={() => !busy && onClose()} width="max-w-[560px]" margin="my-8" panelClassName="p-6">
        <h2 className="mb-3 font-heading text-2xl tracking-wide">Add document</h2>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Type</label>
            <select className={inputCls} value={docType} onChange={(e) => setDocType(e.target.value as ProjectDocType)}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{DOC_TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Title</label><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        </div>
        <div className="mb-3"><label className={labelCls}>Content</label><textarea className={inputCls + " font-mono"} rows={8} value={content} onChange={(e) => setContent(e.target.value)} /></div>
        {err ? <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={add} disabled={busy} className={MODAL_ACTION_BTN}>{busy ? "Adding..." : "Add"}</Button>
        </div>
    </ModalShell>
  );
}

// The subcontractor LIFECYCLE (status, agreements) on the Delivery tab. Subcontractors are
// ADDED, with their cost and markup, on the Costing tab (one home per concern, Session AAB):
// they enter the flow when their quote arrives during costing; their agreements are managed here.
function SubcontractorsSection({ project, reload, isAdmin }: { project: ProjectDetail; reload: () => Promise<void>; isAdmin: boolean }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <h3 className="font-heading text-lg text-ele-strong">Subcontractors</h3>
      <p className="mb-3 mt-1 text-sm text-ele-body">
        Send and track each subcontractor's agreement and status here. Add a subcontractor, with their cost, on the
        Costing tab. {isAdmin ? "Their cost feeds the project margin." : "Costs are visible to admins only."}
      </p>
      {project.subcontractors.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">No subcontractors yet. Add them on the Costing tab as their quotes come in.</p>
      ) : (
        <div className="space-y-2">
          {project.subcontractors.map((s) => (
            <SubcontractorRow key={s.id} projectId={project.id} sub={s} reload={reload} isAdmin={isAdmin} />
          ))}
        </div>
      )}

      <DirectorySubcontractorsSection projectId={project.id} />
    </div>
  );
}

// Links to standalone, vetted directory subcontractors (Session J), distinct from the
// project-scoped subcontractors above. A directory sub carries its own compliance docs and
// signed agreement; here we just point the project at one and record an optional role.
function DirectorySubcontractorsSection({ projectId }: { projectId: string }) {
  const [links, setLinks] = useState<ProjectLinkedSubcontractor[]>([]);
  const [pick, setPick] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session AEH, FIX-2 B3: the directory picker loads via useOptionsLoader so a failed fetch shows
  // an inline "Could not load. Retry" beside the field instead of a silently empty dropdown. The
  // linked-subcontractor list keeps its own loader (it refreshes after a link/unlink).
  const dirLoader = useOptionsLoader<SubcontractorListItem[]>(() => api.subcontractors.list({ status: "active" }), [projectId]);
  const directory = dirLoader.data ?? [];

  const loadLinks = useCallback(() => {
    api.projects.subcontractorLinks(projectId).then(setLinks).catch(() => setLinks([]));
  }, [projectId]);
  useEffect(() => { loadLinks(); }, [loadLinks]);

  const linkedIds = new Set(links.map((l) => l.subcontractorId));
  const options = directory.filter((d) => !linkedIds.has(d.id));

  // A failed link/unlink says so (Session AED: both previously swallowed the error, so the
  // button just stopped being busy with nothing happening and no explanation).
  async function link() {
    if (!pick) return;
    setBusy(true);
    setError(null);
    try {
      await api.projects.linkSubcontractor(projectId, { subcontractorId: pick, role: role.trim() || undefined });
      setPick("");
      setRole("");
      loadLinks();
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not link the subcontractor. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  async function unlink(linkId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.projects.unlinkSubcontractor(projectId, linkId);
      loadLinks();
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not unlink the subcontractor. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 border-t border-ele-purple-accent pt-4">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Directory subcontractors</h3>
      {links.length === 0 ? (
        <p className="text-sm text-ele-body">No directory subcontractors linked.</p>
      ) : (
        <div className="space-y-2">
          {links.map((l) => (
            <div key={l.linkId} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent p-3">
              <div>
                <Link to={`/office/subcontractors/${l.subcontractorId}`} className="font-heading text-ele-strong hover:underline">{l.company}</Link>
                <span className="ml-2 text-sm text-ele-body">{l.trade}{l.role ? ` (${l.role})` : ""}</span>
                <span className="ml-2"><AgreementTag status={l.agreementStatus} /></span>
              </div>
              <OverflowMenu label={`Actions for ${l.company}`}>
                <ConfirmButton onConfirm={() => unlink(l.linkId)} label="Unlink" busy={busy} className={overflowDangerItemCls} />
              </OverflowMenu>
            </div>
          ))}
        </div>
      )}
      {error ? <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{error}</p> : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong">
          <option value="">Link a subcontractor...</option>
          {options.map((d) => <option key={d.id} value={d.id}>{d.company}{d.trade ? ` (${d.trade})` : ""}</option>)}
        </select>
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role (optional)" aria-label="Role on this project" className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong" />
        <Button onClick={link} disabled={busy || !pick} variant="secondary" size="sm">Link</Button>
      </div>
      {dirLoader.error ? (
        <p className="mt-2 text-xs text-ele-danger">
          Could not load the subcontractor directory.{" "}
          <button type="button" onClick={dirLoader.retry} className="underline hover:text-ele-strong">Retry</button>
        </p>
      ) : null}
    </div>
  );
}

const SUB_STATUSES = Object.keys(SUBCONTRACTOR_STATUS_LABELS) as ProjectSubcontractorStatus[];

function SubcontractorRow({ projectId, sub, reload, isAdmin }: { projectId: string; sub: ProjectSubcontractor; reload: () => Promise<void>; isAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  async function setStatus(status: ProjectSubcontractorStatus) {
    setBusy(true);
    try {
      await api.projects.updateSubcontractor(projectId, sub.id, { status });
      await reload();
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.removeSubcontractor(projectId, sub.id);
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not remove.");
      setBusy(false);
    }
  }
  async function sendAgreement() {
    if (
      !(await confirm({
        title: "Send the agreement?",
        message: sub.email
          ? `Send the subcontractor agreement to ${sub.email} for signature?`
          : `Send the subcontractor agreement to ${sub.company} for signature?`,
        confirmLabel: "Send for signature",
      }))
    )
      return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.projects.sendSubcontractorAgreement(projectId, sub.id);
      // Session RC2: the covering note is a Gmail draft; say what became of it, never assume.
      setMsg(res.result === "sent" ? `Agreement sent for signature.${res.coveringDraftNote ? ` ${res.coveringDraftNote}` : ""}` : "The agreement was already sent.");
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not send the agreement.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {confirmDialog}
      {/* Session AAX Loop 4: the row itself opens the editor (no Edit button); clicks on the
          row's own controls (select, buttons, links) never open it. Keyboard: Enter/Space on
          the focused row opens too (inner controls keep their own keys). */}
    <div
      role="button"
      tabIndex={0}
      aria-label={`Edit ${sub.company}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, select, a, input, label, textarea")) return;
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      className="cursor-pointer rounded-ele border border-ele-purple-accent p-4 outline-none transition hover:border-ele-green focus-visible:border-ele-green"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading text-ele-strong">{sub.company}</div>
          <div className="text-sm text-ele-body">{sub.contactName ?? "-"}{sub.email ? ` | ${sub.email}` : ""}{sub.phone ? ` | ${sub.phone}` : ""}</div>
          {sub.scope ? <div className="mt-1 text-sm text-ele-body">{sub.scope}</div> : null}
          <div className="mt-1 text-sm text-ele-body">
            {isAdmin ? <span className="mr-3">Cost: {gbp(sub.cost)}</span> : null}
            {sub.startDate || sub.endDate ? <span>{formatDate(sub.startDate)} to {formatDate(sub.endDate)}</span> : null}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <select className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs outline-none focus:border-ele-green" value={sub.status} disabled={busy} onChange={(e) => setStatus(e.target.value as ProjectSubcontractorStatus)}>
            {SUB_STATUSES.map((st) => <option key={st} value={st}>{SUBCONTRACTOR_STATUS_LABELS[st]}</option>)}
          </select>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sub.agreementSignedAt ? (
              <StatusPill domain="projectSubcontractor" status="agreement_signed" title={`Signed ${formatDate(sub.agreementSignedAt)}`} />
            ) : sub.hasAgreement ? (
              <StatusPill domain="projectSubcontractor" status="agreement_sent" title={sub.agreementSentAt ? `Sent ${formatDate(sub.agreementSentAt)}` : undefined} />
            ) : (
              // Session AAB colour rule: an action is green, never informational blue (this
              // finishes the AV "Signable stays blue" carve-out retirement; ghost, not solid,
              // as it is one action within a row).
              <Button onClick={sendAgreement} disabled={busy} variant="secondary" size="sm">Send agreement</Button>
            )}
            {sub.agreementPdfUrl ? (
              <a href={sub.agreementPdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-ele-green hover:underline">View signed</a>
            ) : null}
            <OverflowMenu label={`Actions for ${sub.company}`}>
              <ConfirmButton onConfirm={remove} label="Remove" busy={busy} busyLabel="Removing..." disabled={sub.hasAgreement} title={sub.hasAgreement ? "An agreement has been sent; cannot remove" : undefined} className={overflowDangerItemCls} />
            </OverflowMenu>
          </div>
        </div>
      </div>
      {msg ? <p role="alert" className="mt-2 text-sm text-ele-danger">{msg}</p> : null}
      {editing ? <SubcontractorModal projectId={projectId} sub={sub} isAdmin={isAdmin} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await reload(); }} /> : null}
    </div>
    </>
  );
}

function SubcontractorModal({ projectId, sub, isAdmin, onClose, onSaved }: { projectId: string; sub?: ProjectSubcontractor; isAdmin?: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    company: sub?.company ?? "",
    contactName: sub?.contactName ?? "",
    email: sub?.email ?? "",
    phone: sub?.phone ?? "",
    scope: sub?.scope ?? "",
    cost: sub?.cost != null ? String(sub.cost) : "",
    startDate: sub?.startDate ? sub.startDate.slice(0, 10) : "",
    endDate: sub?.endDate ? sub.endDate.slice(0, 10) : "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Session ABB, Loop 4: at costing time only the company and its cost are truly needed; the
  // contact, dates and scope tuck behind the same "More details" disclosure the New Project
  // modal uses. Opens pre-expanded when editing a subcontractor that already carries any of
  // those values, so nothing on file is ever hidden.
  const [showMore, setShowMore] = useState(
    Boolean(sub && (sub.contactName || sub.email || sub.phone || sub.scope || sub.startDate || sub.endDate)),
  );
  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    if (!form.company.trim()) {
      setErr("Enter the company name.");
      return;
    }
    setBusy(true);
    setErr(null);
    const num = (s: string): number | undefined => {
      const x = parseFloat(s);
      return s.trim() !== "" && Number.isFinite(x) ? x : undefined;
    };
    const body: ProjectSubcontractorInput = {
      company: form.company.trim(),
      contactName: form.contactName || undefined,
      email: form.email || undefined,
      phone: form.phone || undefined,
      scope: form.scope || undefined,
      cost: num(form.cost),
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
    };
    try {
      if (sub) await api.projects.updateSubcontractor(projectId, sub.id, body);
      else await api.projects.addSubcontractor(projectId, body);
      await onSaved();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not save."));
      setBusy(false);
    }
  }
  return (
    <ModalShell ariaLabel={sub ? "Edit subcontractor" : "Add subcontractor"} onClose={() => !busy && onClose()} width="max-w-[560px]" margin="my-8" panelClassName="p-6">
        <h2 className="mb-3 font-heading text-2xl tracking-wide">{sub ? "Edit subcontractor" : "Add subcontractor"}</h2>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><label className={labelCls}>Company</label><input className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="e.g. ILE" /></div>
          {isAdmin !== false ? (
            <div><label className={labelCls}>Cost to ELE (ex VAT)</label><input type="number" className={inputCls} value={form.cost} onChange={(e) => set("cost", e.target.value)} /></div>
          ) : null}
        </div>
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowMore((o) => !o)}
            aria-expanded={showMore}
            className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
          >
            {showMore ? "Hide extra details" : "More details (optional)"}
          </button>
          {!showMore ? <span className="ml-2 text-xs text-ele-body opacity-70">Contact, dates and scope. All can be added later.</span> : null}
        </div>
        {showMore ? (
          <>
            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Contact</label><input className={inputCls} value={form.contactName} onChange={(e) => set("contactName", e.target.value)} /></div>
              <div><label className={labelCls}>Email</label><input className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div><label className={labelCls}>Start</label><input type="date" className={inputCls} value={form.startDate} onChange={(e) => set("startDate", e.target.value)} /></div>
                <div><label className={labelCls}>End</label><input type="date" className={inputCls} value={form.endDate} onChange={(e) => set("endDate", e.target.value)} /></div>
              </div>
            </div>
            <div className="mb-3"><label className={labelCls}>Scope of works</label><textarea className={inputCls} rows={3} value={form.scope} onChange={(e) => set("scope", e.target.value)} /></div>
          </>
        ) : null}
        {err ? <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={save} disabled={busy} className={MODAL_ACTION_BTN}>{busy ? "Saving..." : "Save"}</Button>
        </div>
    </ModalShell>
  );
}

// ---- Site survey section (on the Details tab) ------------------------------
// The site survey: free-text condition notes (which feed the scope-of-works AI draft and the
// quotation's existing-installation summary) plus the supporting photos. The scope draft itself
// is generated on the Quote tab (the ONE canonical generate; Session AAB killed the duplicate).
function SurveySection({ project, reload }: { project: ProjectDetail; reload: () => Promise<void> }) {
  const [notes, setNotes] = useState(project.conditionNotes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMsg, setNotesMsg] = useState<string | null>(null);

  // Re-seed when the project reloads elsewhere (useState only seeds on first mount).
  useEffect(() => {
    setNotes(project.conditionNotes ?? "");
  }, [project.conditionNotes]);

  async function saveNotes() {
    setSavingNotes(true);
    setNotesMsg(null);
    try {
      await api.projects.update(project.id, { conditionNotes: notes });
      await reload();
      setNotesMsg("Saved.");
    } catch (e) {
      setNotesMsg(e instanceof ApiError ? e.message || e.code : "Could not save the notes.");
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <h3 className="font-heading text-lg text-ele-strong">Site survey</h3>
        <p className="mt-1 text-sm text-ele-body">
          What you found on site: the existing controller make and model, condition, what is worn or obsolete. These notes
          feed the scope of works draft on the Quote tab and the quotation's existing-installation summary.
        </p>
        <textarea
          className={inputCls + " mt-2"}
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            // Auto-save on blur (Session AAB review F4; the SAVING MODEL since Session ABB, Loop 2):
            // clicking the next-step banner CTA (or a tab) blurs the field first, so typed notes are
            // persisted before the section unmounts and the Quote-tab scope generation always sees
            // them. The explicit Save button was retired -- autosave with a visible status is the
            // one save path, so there is no button to remember.
            if (!savingNotes && notes !== (project.conditionNotes ?? "")) void saveNotes();
          }}
          aria-label="Condition notes"
          placeholder="e.g. 2 hydraulic passenger lifts. Existing controllers are 1990s relay logic, obsolete, spares no longer available. Door operators worn."
        />
        <p className="mt-2 text-xs text-ele-body opacity-70">
          Notes save automatically when you click away.{" "}
          {savingNotes ? <span className="text-ele-body">Saving...</span> : notesMsg ? <span role="status" className="text-ele-green">{notesMsg}</span> : null}
        </p>
      </div>
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <ProjectPhotos projectId={project.id} />
      </div>
    </div>
  );
}

// ---- Costing tab (stage 2) --------------------------------------------------
// The internal cost working document: ELE labour cost lines, per-supplier material cost lines and
// the subcontractor costs, rolled up against the project value to give the total cost to ELE and
// the margin (admin-only). The payment schedule is auto-calculated. The client never sees this
// breakdown; they see only the project value and the payment schedule. Stage invoicing is a
// post-acceptance activity and lives on the Delivery tab (Session AAB).
function CostsTab({ project, reload, isAdmin }: { project: ProjectDetail; reload: () => Promise<void>; isAdmin: boolean }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-ele-body">
        Build the price as the supplier and subcontractor quotes come in. The customer never sees this breakdown: the
        quotation shows only the project value and the payment schedule.
      </p>
      {isAdmin ? (
        <CostEditor project={project} reload={reload} />
      ) : (
        <>
          <div data-focus-section="costing-lines" className="rounded-ele border border-ele-purple-accent p-4">
            <h3 className="font-heading text-lg text-ele-strong">Project value</h3>
            <p className="mt-1 text-2xl text-ele-green">{gbp(project.contractValue)}</p>
            <p className="text-xs text-ele-body">ex VAT</p>
            <p className="mt-2 text-sm text-ele-body">The internal cost breakdown and margin are visible to admins only.</p>
            <PaymentSchedule milestones={project.milestones} contractValue={project.contractValue} />
          </div>
          <OfficeSubcontractorsCard project={project} reload={reload} />
        </>
      )}
    </div>
  );
}

// The office (non-admin) subcontractor entry point on the Costing tab: subcontractors still
// enter the flow here (the server allows office to create them); costs and markups stay
// admin-only, so the card shows no figures. The Delivery tab manages status and agreements.
function OfficeSubcontractorsCard({ project, reload }: { project: ProjectDetail; reload: () => Promise<void> }) {
  const [modal, setModal] = useState<{ sub?: ProjectSubcontractor } | null>(null);
  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-heading text-lg text-ele-strong">Subcontractors</h3>
        <button onClick={() => setModal({})} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>Add subcontractor</button>
      </div>
      <p className="mb-2 text-sm text-ele-body">Add each subcontractor as their quote comes in. Costs are visible to admins only; agreements and status are managed on the Delivery tab.</p>
      {project.subcontractors.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">No subcontractors yet.</p>
      ) : (
        <div className="space-y-2">
          {project.subcontractors.map((s) => (
            // Loop 4: the row opens its editor (no Edit button).
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              aria-label={`Edit ${s.company}`}
              onClick={() => setModal({ sub: s })}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setModal({ sub: s });
                }
              }}
              className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2 outline-none transition hover:border-ele-green focus-visible:border-ele-green"
            >
              <div>
                <span className="font-heading text-ele-strong">{s.company}</span>
                {s.scope ? <span className="ml-2 text-sm text-ele-body">{s.scope}</span> : null}
                <span className="ml-2 rounded-full bg-ele-inset px-2 py-0.5 text-[10px] uppercase text-ele-body">{SUBCONTRACTOR_STATUS_LABELS[s.status]}</span>
              </div>
              <span className="font-menu text-[11px] uppercase tracking-wide text-ele-body opacity-60">Edit</span>
            </div>
          ))}
        </div>
      )}
      {modal ? <SubcontractorModal projectId={project.id} sub={modal.sub} isAdmin={false} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await reload(); }} /> : null}
    </div>
  );
}

// The full cost editor (admin). Labour + supplier rows are edited locally and persisted together on
// "Save costs"; the totals, margin and payment schedule update LIVE as the office types. The
// subcontractor cost rows are the project's subcontractors (lifecycle + agreements managed on the Delivery tab).
//
// Session AAA Item 3 (cost + markup = charge): every cost line carries a markup percent (default
// 30); the charge to the client is derived per line, and the PROJECT VALUE is CALCULATED (all
// charges + the overall margin percent), never typed. A legacy line saved before the markup model
// is seeded at the 30 default when the editor opens.
function CostEditor({ project, reload }: { project: ProjectDetail; reload: () => Promise<void> }) {
  // Session ABB, Loop 2: a fresh costing starts from an editable pattern, not a blank state --
  // one empty labour row and one empty supplier row are pre-seeded when the project has no cost
  // lines at all, so the office types straight into them instead of building the rows first.
  // Untouched empty rows are filtered out on save (persistCosts), so nothing empty is stored.
  const storedLabour = (project.costBreakdown?.labour ?? []).map((l) => ({ ...l, markupPercent: l.markupPercent ?? DEFAULT_MARKUP_PERCENT }));
  const storedSuppliers = (project.costBreakdown?.suppliers ?? []).map((s) => ({ ...s, markupPercent: s.markupPercent ?? DEFAULT_MARKUP_PERCENT }));
  const seedEmpty = storedLabour.length === 0 && storedSuppliers.length === 0;
  const [labour, setLabour] = useState<ProjectLabourLine[]>(() =>
    seedEmpty ? [{ description: "", basis: "day_rate" as ProjectLabourBasis, quantity: 1, unitCost: 0, markupPercent: DEFAULT_MARKUP_PERCENT }] : storedLabour,
  );
  const [suppliers, setSuppliers] = useState<ProjectSupplierCostLine[]>(() =>
    seedEmpty ? [{ company: "", supplies: "", leadTime: "", cost: 0, markupPercent: DEFAULT_MARKUP_PERCENT }] : storedSuppliers,
  );
  const [subMarkups, setSubMarkups] = useState<Record<string, number>>(project.costBreakdown?.subcontractorMarkups ?? {});
  const [overallMargin, setOverallMargin] = useState(String(project.costBreakdown?.overallMarginPercent ?? 0));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgError, setMsgError] = useState(false);
  const [subModal, setSubModal] = useState<{ sub?: ProjectSubcontractor } | null>(null);
  // Session AV, Item 1: supplier quote PDFs are attached here, per supplier cost row. Load the
  // project's supplier_quote documents so a row can show/download/remove its own PDF and any quotes
  // not linked to a row (uploaded earlier or left behind by a deleted row) can still be cleaned up.
  const [supplierDocs, setSupplierDocs] = useState<ProjectDocument[]>([]);
  const [attachBusy, setAttachBusy] = useState<number | null>(null);

  const loadSupplierDocs = useCallback(async () => {
    try {
      const all = await api.projects.documents(project.id);
      setSupplierDocs(all.filter((d) => d.docType === "supplier_quote"));
    } catch {
      /* leave the list as-is; the per-row attach/remove still work */
    }
  }, [project.id]);
  useEffect(() => {
    void loadSupplierDocs();
  }, [loadSupplierDocs]);

  // When a quotation has been built from line items, that sum OWNS the project value (the TOTALS
  // RULE), so the calculated figure is display-only context here and the stored value stays the
  // line-item sum until the quotation is cleared.
  const valueLockedByQuotation = (project.quotationDetail?.lineItems.length ?? 0) > 0;

  // Clamped + 0.1-rounded exactly like every other markup input (and the server's asMarkup),
  // so the live preview never shows a figure the save would reject or store differently.
  const overallMarginNum = (() => {
    const x = parseFloat(overallMargin);
    if (!Number.isFinite(x) || x < 0) return 0;
    return Math.round(Math.min(x, 500) * 10) / 10;
  })();
  const subRows = project.subcontractors.map((s) => ({ id: s.id, cost: s.cost }));
  // The CALCULATED project value (all line charges + the overall margin). When the quotation owns
  // the value, the stored line-item sum is what everything downstream reads.
  const computedValue = computeProjectValueFromCosts({
    labour,
    suppliers,
    subcontractors: subRows,
    subcontractorMarkups: subMarkups,
    overallMarginPercent: overallMarginNum,
  });
  const effectiveValue = valueLockedByQuotation ? project.contractValue : computedValue;
  const subCosts = project.subcontractors.map((s) => s.cost);
  const summary = computeClientCostSummary({ labour, suppliers, subcontractorCosts: subCosts, projectValue: effectiveValue });
  const milestones = previewMilestones({ projectValue: summary.projectValue, supplierCost: summary.supplierCost, subcontractorCost: summary.subcontractorCost });

  function setLabourRow(i: number, patch: Partial<ProjectLabourLine>) {
    setLabour((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function setSupplierRow(i: number, patch: Partial<ProjectSupplierCostLine>) {
    setSuppliers((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  // ---- Typing-speed line entry (Session AAX, Loop 3) ----
  // Tab moves across a row natively; Enter adds the next line of the SAME section and puts the
  // cursor in its first field, so a multi-line costing never needs the mouse.
  function focusCostRow(kind: "labour" | "supplier", index: number) {
    window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-cost-row="${kind}-${index}"] input`)?.focus();
    }, 30);
  }
  function addLabourRow(focusNew = false) {
    if (focusNew) focusCostRow("labour", labour.length); // the new row's index (side effect kept OUT of the updater)
    setLabour((r) => [...r, { description: "", basis: "day_rate" as ProjectLabourBasis, quantity: 1, unitCost: 0, markupPercent: DEFAULT_MARKUP_PERCENT }]);
  }
  function addSupplierRow(focusNew = false) {
    if (focusNew) focusCostRow("supplier", suppliers.length);
    setSuppliers((r) => [...r, { company: "", supplies: "", leadTime: "", cost: 0, markupPercent: DEFAULT_MARKUP_PERCENT }]);
  }
  function rowKeyDown(kind: "labour" | "supplier") {
    return (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Enter") return;
      const target = e.target as HTMLElement;
      // Only plain text/number inputs: selects, buttons and the file-attach label keep
      // their native Enter behaviour.
      if (target.tagName !== "INPUT" || (target as HTMLInputElement).type === "file") return;
      e.preventDefault();
      if (kind === "labour") addLabourRow(true);
      else addSupplierRow(true);
    };
  }

  // Persist the given cost rows then reload the project. Shared by Save costs and by the per-row
  // PDF attach/remove (so the attachment link survives a reload). Match the server's non-empty
  // predicates (parseCostBreakdown): keep a row that only carries an attached PDF; never send NaN.
  // The project value is NOT sent: the server computes it from these costs + markups.
  async function persistCosts(sup: ProjectSupplierCostLine[], lab: ProjectLabourLine[]) {
    await api.projects.saveCosts(project.id, {
      labour: lab.filter((l) => l.description.trim() || l.unitCost > 0),
      suppliers: sup.filter((s) => s.company.trim() || s.supplies.trim() || s.leadTime.trim() || s.cost > 0 || Boolean(s.quoteDocId)),
      subcontractorMarkups: subMarkups,
      overallMarginPercent: overallMarginNum,
    });
    await reload();
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    setMsgError(false);
    try {
      await persistCosts(suppliers, labour);
      setMsg("Costs saved.");
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not save the costs.");
    } finally {
      setSaving(false);
    }
  }

  // Attach a supplier quote PDF to a specific supplier cost row. Uploads via the existing supplier
  // quote endpoint (files it to Drive, records a ProjectDocument the AI scope generation reads),
  // links it to this row and persists so the link survives a reload.
  async function onSupplierFile(i: number, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) return;
    setAttachBusy(i);
    setMsg(null);
    setMsgError(false);
    try {
      const doc = await api.projects.uploadSupplierQuote(project.id, { file, supplier: suppliers[i]?.company.trim() || "Supplier quote" });
      const updated = suppliers.map((r, idx) => (idx === i ? { ...r, quoteDocId: doc.id, quoteUrl: doc.driveUrl ?? "", quoteName: file.name } : r));
      setSuppliers(updated);
      await persistCosts(updated, labour);
      await loadSupplierDocs();
    } catch (err) {
      setMsgError(true);
      setMsg(err instanceof ApiError ? err.message || err.code : "Could not attach the supplier quote PDF.");
    } finally {
      setAttachBusy(null);
    }
  }

  // Remove the PDF attached to a supplier row: delete the document (and its Drive file), clear the
  // row reference.
  async function removeSupplierPdf(i: number) {
    const docId = suppliers[i]?.quoteDocId;
    setAttachBusy(i);
    setMsg(null);
    setMsgError(false);
    try {
      if (docId) await api.projects.removeDocument(project.id, docId);
      const updated = suppliers.map((r, idx) => (idx === i ? { ...r, quoteDocId: undefined, quoteUrl: undefined, quoteName: undefined } : r));
      setSuppliers(updated);
      await persistCosts(updated, labour);
      await loadSupplierDocs();
    } catch (err) {
      setMsgError(true);
      setMsg(err instanceof ApiError ? err.message || err.code : "Could not remove the supplier quote PDF.");
    } finally {
      setAttachBusy(null);
    }
  }

  // Remove a supplier quote PDF that is not linked to any current cost row.
  async function removeOtherDoc(docId: string) {
    setMsg(null);
    setMsgError(false);
    try {
      await api.projects.removeDocument(project.id, docId);
      await loadSupplierDocs();
    } catch (err) {
      setMsgError(true);
      setMsg(err instanceof ApiError ? err.message || err.code : "Could not remove the supplier quote PDF.");
    }
  }

  const num = (s: string): number => {
    const x = parseFloat(s);
    return Number.isFinite(x) && x > 0 ? x : 0;
  };
  // Session ABB (found by the measured walkthrough): a numeric cost field that already shows a
  // value (the seeded Days "1", a markup "30") APPENDS when typed into -- "12" became 112 days
  // and silently inflated the project value. Selecting the content on focus makes typing
  // replace, the behaviour a fast typist expects.
  const selectOnFocus = (e: { currentTarget: { select: () => void } }) => e.currentTarget.select();
  // A markup/margin percent from an input: clamped to [0, 500] and rounded to 0.1, EXACTLY
  // mirroring the server's asMarkup normalisation, so the live preview always equals what the
  // save will store (no post-save penny disagreement).
  const pct = (s: string): number => {
    const x = parseFloat(s);
    if (!Number.isFinite(x) || x < 0) return 0;
    return Math.round(Math.min(x, 500) * 10) / 10;
  };

  // Supplier quote PDFs not linked to any current cost row (uploaded before this row model, or left
  // behind when a row was deleted) -- still shown so they can be downloaded or removed.
  const linkedDocIds = new Set(suppliers.map((s) => s.quoteDocId).filter(Boolean) as string[]);
  const otherSupplierDocs = supplierDocs.filter((d) => !linkedDocIds.has(d.id));

  return (
    <div className="space-y-4">
      {/* THE PINNED LIVE TOTALS BAR (Session AAX, Loop 3): cost to ELE, project value ex VAT
          and margin stay visible while lines are entered, updating on every keystroke. Save
          lives here too, always in reach. Sits under the 62px sticky office header. */}
      <div
        role="status"
        aria-label="Live costing totals"
        className="sticky top-[62px] z-10 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] px-4 py-2"
      >
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span>
            <span className="mr-2 font-menu text-[10px] uppercase tracking-widest text-ele-body">Cost to ELE</span>
            <span className="text-sm text-ele-strong">{gbp(summary.totalCostToEle)}</span>
          </span>
          <span>
            <span className="mr-2 font-menu text-[10px] uppercase tracking-widest text-ele-body">Value ex VAT</span>
            <span className="font-heading text-lg text-ele-green" aria-label="Live project value">{gbp(effectiveValue)}</span>
          </span>
          <span>
            <span className="mr-2 font-menu text-[10px] uppercase tracking-widest text-ele-body">Margin</span>
            {summary.margin == null ? (
              <span className="text-sm text-ele-body">-</span>
            ) : summary.margin < 0 ? (
              <span className="text-sm font-bold text-amber-400">{gbp(summary.margin)}</span>
            ) : (
              <span className="text-sm font-bold text-ele-green">{gbp(summary.margin)}{summary.marginPct != null ? ` (${summary.marginPct}%)` : ""}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {msg ? <span role={msgError ? "alert" : "status"} className={`text-xs ${msgError ? "text-ele-danger" : "text-ele-green"}`}>{msg}</span> : null}
          <Button onClick={save} disabled={saving} size="sm">{saving ? "Saving..." : "Save costs"}</Button>
        </div>
      </div>

      {/* ELE LABOUR */}
      <div data-focus-section="costing-lines" className="rounded-ele border border-ele-purple-accent p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-lg text-ele-strong">ELE labour</h3>
          <button onClick={() => addLabourRow()} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>Add labour line</button>
        </div>
        {labour.length === 0 ? (
          <p className="text-sm text-ele-body opacity-70">No labour lines. Add ELE's own labour cost for this project.</p>
        ) : (
          // Enter adds the next labour line and focuses it; Tab moves across the row (Loop 3).
          <div className="space-y-2" onKeyDown={rowKeyDown("labour")}>
            {labour.map((l, i) => (
              <div key={i} data-cost-row={`labour-${i}`} className="flex flex-wrap items-end gap-2">
                <div className="min-w-48 flex-1"><label className={labelCls}>Description</label><input className={inputCls} value={l.description} onChange={(e) => setLabourRow(i, { description: e.target.value })} placeholder="e.g. Strip out and install" /></div>
                <div><label className={labelCls}>Basis</label>
                  <select className={inputCls} value={l.basis} onChange={(e) => setLabourRow(i, { basis: e.target.value as ProjectLabourBasis })}>
                    <option value="day_rate">Day rate</option>
                    <option value="fixed">Fixed price</option>
                  </select>
                </div>
                {l.basis === "day_rate" ? (
                  <>
                    <div className="w-24"><label className={labelCls}>Days</label><input type="number" className={inputCls} value={l.quantity || ""} onFocus={selectOnFocus} onChange={(e) => setLabourRow(i, { quantity: num(e.target.value) })} /></div>
                    <div className="w-28"><label className={labelCls}>Day rate</label><input type="number" className={inputCls} value={l.unitCost || ""} onFocus={selectOnFocus} onChange={(e) => setLabourRow(i, { unitCost: num(e.target.value) })} /></div>
                  </>
                ) : (
                  <div className="w-32"><label className={labelCls}>Amount</label><input type="number" className={inputCls} value={l.unitCost || ""} onFocus={selectOnFocus} onChange={(e) => setLabourRow(i, { unitCost: num(e.target.value) })} /></div>
                )}
                <div className="w-20"><label className={labelCls}>Markup %</label><input type="number" aria-label={`Labour markup percent line ${i + 1}`} className={inputCls} value={l.markupPercent ?? DEFAULT_MARKUP_PERCENT} onFocus={selectOnFocus} onChange={(e) => setLabourRow(i, { markupPercent: pct(e.target.value) })} /></div>
                <div className="w-32 text-right" aria-label="Labour charge to client">
                  <div className="text-sm text-ele-strong">{gbp(labourLineCharge(l))}</div>
                  <div className="text-[10px] text-ele-body opacity-70">cost {gbp(labourLineCost(l))}</div>
                </div>
                <button onClick={() => setLabour((r) => r.filter((_, idx) => idx !== i))} className="self-end px-2 pb-1.5 text-lg leading-none text-ele-body transition hover:text-ele-danger" aria-label="Remove labour line" title="Remove this line">&times;</button>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-ele-body opacity-60">Tab moves across the line. Enter adds the next line.</p>
      </div>

      {/* SUPPLIERS */}
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-lg text-ele-strong">Suppliers (parts and materials)</h3>
          <button onClick={() => addSupplierRow()} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>Add supplier</button>
        </div>
        {suppliers.length === 0 ? (
          <p className="text-sm text-ele-body opacity-70">No suppliers. Add a supplier as each parts quote comes in, and attach its quote PDF to the row.</p>
        ) : (
          <div className="space-y-2" onKeyDown={rowKeyDown("supplier")}>
            {suppliers.map((s, i) => (
              <div key={i} data-cost-row={`supplier-${i}`} className="flex flex-wrap items-end gap-2">
                <div className="w-40"><label className={labelCls}>Company</label><input className={inputCls} value={s.company} onChange={(e) => setSupplierRow(i, { company: e.target.value })} placeholder="e.g. ILE" /></div>
                <div className="min-w-48 flex-1"><label className={labelCls}>Supplying</label><input className={inputCls} value={s.supplies} onChange={(e) => setSupplierRow(i, { supplies: e.target.value })} placeholder="e.g. Controller package" /></div>
                <div className="w-28"><label className={labelCls}>Lead time</label><input className={inputCls} value={s.leadTime} onChange={(e) => setSupplierRow(i, { leadTime: e.target.value })} placeholder="8 weeks" /></div>
                <div className="w-28"><label className={labelCls}>Cost</label><input type="number" className={inputCls} value={s.cost || ""} onFocus={selectOnFocus} onChange={(e) => setSupplierRow(i, { cost: num(e.target.value) })} /></div>
                <div className="w-20"><label className={labelCls}>Markup %</label><input type="number" aria-label={`Supplier markup percent row ${i + 1}`} className={inputCls} value={s.markupPercent ?? DEFAULT_MARKUP_PERCENT} onFocus={selectOnFocus} onChange={(e) => setSupplierRow(i, { markupPercent: pct(e.target.value) })} /></div>
                <div className="w-32 text-right" aria-label="Supplier charge to client">
                  <div className="text-sm text-ele-strong">{gbp(supplierLineCharge(s))}</div>
                  <div className="text-[10px] text-ele-body opacity-70">cost {gbp(s.cost)}</div>
                </div>
                {/* Session AV, Item 1: attach the supplier's quote PDF to this row (cost input, not a document). */}
                {s.quoteUrl || s.quoteDocId ? (
                  <div className="flex items-center gap-2">
                    {s.quoteUrl ? (
                      <a href={s.quoteUrl} target="_blank" rel="noopener noreferrer" className="max-w-40 truncate text-xs text-ele-green underline" title={s.quoteName || "Supplier quote"}>{s.quoteName || "Supplier quote"}</a>
                    ) : (
                      <span className="text-xs text-ele-body">PDF attached</span>
                    )}
                    {/* Loop 4: deleting the filed PDF is destructive -> overflow + two-step. */}
                    <OverflowMenu label={`Supplier quote actions line ${i + 1}`}>
                      <ConfirmButton onConfirm={() => void removeSupplierPdf(i)} label="Remove PDF" busy={attachBusy === i} className={overflowDangerItemCls} />
                    </OverflowMenu>
                  </div>
                ) : (
                  <label className={`${btnSm} cursor-pointer border-ele-purple-accent text-ele-body hover:text-ele-green ${attachBusy === i ? "opacity-50" : ""}`}>
                    {attachBusy === i ? "Attaching..." : "Attach PDF"}
                    <input type="file" accept="application/pdf,.pdf" className="hidden" aria-label={`Attach supplier quote PDF for line ${i + 1}`} disabled={attachBusy === i} onChange={(e) => void onSupplierFile(i, e)} />
                  </label>
                )}
                <button onClick={() => setSuppliers((r) => r.filter((_, idx) => idx !== i))} className="self-end px-2 pb-1.5 text-lg leading-none text-ele-body transition hover:text-ele-danger" aria-label="Remove supplier" title="Remove this supplier line">&times;</button>
              </div>
            ))}
          </div>
        )}
        {otherSupplierDocs.length > 0 ? (
          <div className="mt-3 border-t border-ele-purple-accent pt-3">
            <p className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">Other supplier quotes (not linked to a row)</p>
            <div className="space-y-1">
              {otherSupplierDocs.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                  {d.driveUrl ? <a href={d.driveUrl} target="_blank" rel="noopener noreferrer" className="text-ele-green underline">{d.title}</a> : <span className="text-ele-body">{d.title}</span>}
                  {d.fileSizeBytes != null ? <span className="text-xs text-ele-body">{formatBytes(d.fileSizeBytes)}</span> : null}
                  <OverflowMenu label={`Actions for ${d.title}`}>
                    <ConfirmButton onConfirm={() => void removeOtherDoc(d.id)} label="Remove" className={overflowDangerItemCls} />
                  </OverflowMenu>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* SUBCONTRACTORS (cost rows; lifecycle + agreements on the Delivery tab) */}
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-lg text-ele-strong">Subcontractors</h3>
          <button onClick={() => setSubModal({})} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>Add subcontractor</button>
        </div>
        {project.subcontractors.length === 0 ? (
          <p className="text-sm text-ele-body opacity-70">No subcontractors. Add a bona-fide subcontractor doing labour; send their agreement on the Delivery tab.</p>
        ) : (
          <div className="space-y-2">
            {project.subcontractors.map((s) => (
              // Loop 4: the row opens its editor; the markup input keeps its own clicks.
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                aria-label={`Edit ${s.company}`}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button, select, a, input, label, textarea")) return;
                  setSubModal({ sub: s });
                }}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSubModal({ sub: s });
                  }
                }}
                className="flex cursor-pointer flex-wrap items-end justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2 outline-none transition hover:border-ele-green focus-visible:border-ele-green"
              >
                <div className="min-w-48 flex-1">
                  <span className="font-heading text-ele-strong">{s.company}</span>
                  {s.scope ? <span className="ml-2 text-sm text-ele-body">{s.scope}</span> : null}
                  <span className="ml-2 rounded-full bg-ele-inset px-2 py-0.5 text-[10px] uppercase text-ele-body">{SUBCONTRACTOR_STATUS_LABELS[s.status]}</span>
                </div>
                <div className="w-20">
                  <label className={labelCls}>Markup %</label>
                  <input
                    type="number"
                    aria-label={`Subcontractor markup percent for ${s.company}`}
                    className={inputCls}
                    value={subMarkups[s.id] ?? DEFAULT_MARKUP_PERCENT}
                    onFocus={selectOnFocus}
                    onChange={(e) => setSubMarkups((m) => ({ ...m, [s.id]: pct(e.target.value) }))}
                  />
                </div>
                <div className="w-32 text-right" aria-label={`Subcontractor charge to client for ${s.company}`}>
                  <div className="text-sm text-ele-strong">{gbp(chargeFor(s.cost ?? 0, subMarkups[s.id]))}</div>
                  <div className="text-[10px] text-ele-body opacity-70">cost {gbp(s.cost)}</div>
                </div>
                <span className="self-center font-menu text-[11px] uppercase tracking-wide text-ele-body opacity-60">Edit</span>
              </div>
            ))}
          </div>
        )}
        {subModal ? <SubcontractorModal projectId={project.id} sub={subModal.sub} isAdmin onClose={() => setSubModal(null)} onSaved={async () => { setSubModal(null); await reload(); }} /> : null}
      </div>

      {/* TOTALS + overall margin + the CALCULATED project value + margin */}
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <h3 className="mb-2 font-heading text-lg text-ele-strong">Totals</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr><td className="py-1 text-ele-body">Total ELE labour</td><td className="py-1 text-right text-ele-strong">{gbp(summary.labourCost)}</td></tr>
            <tr><td className="py-1 text-ele-body">Total suppliers</td><td className="py-1 text-right text-ele-strong">{gbp(summary.supplierCost)}</td></tr>
            <tr><td className="py-1 text-ele-body">Total subcontractors</td><td className="py-1 text-right text-ele-strong">{gbp(summary.subcontractorCost)}</td></tr>
            <tr className="border-t border-ele-purple-accent"><td className="py-1 font-bold text-ele-body">Total cost to ELE</td><td className="py-1 text-right font-bold text-ele-strong">{gbp(summary.totalCostToEle)}</td></tr>
          </tbody>
        </table>
        <div className="mt-3">
          <label className={labelCls} htmlFor="costs-overall-margin">Overall margin % (optional contingency, on top of all charges)</label>
          <input id="costs-overall-margin" type="number" min={0} max={500} className={inputCls + " max-w-32"} value={overallMargin} onFocus={selectOnFocus} onChange={(e) => setOverallMargin(e.target.value)} />
        </div>
        <div className="mt-3 rounded-ele border border-ele-green/40 bg-ele-surface px-3 py-2">
          <div className="font-menu text-[11px] uppercase tracking-wide text-ele-body">Project value (ex VAT): what the customer pays, calculated from the charges above</div>
          <div className="text-2xl text-ele-green" aria-label="Calculated project value">{gbp(effectiveValue)}</div>
          {valueLockedByQuotation ? (
            <p className="mt-1 text-xs text-ele-body opacity-70">A quotation built from line items owns the value (their sum). The calculated figure from these costs is {gbp(computedValue)}.</p>
          ) : effectiveValue == null ? (
            <p className="mt-1 text-xs text-ele-body opacity-70">Add cost lines above; the value is calculated from cost plus markup.</p>
          ) : computedValue !== project.contractValue ? (
            <p className="mt-1 text-xs text-amber-300">
              Differs from the stored value ({gbp(project.contractValue)}) until costs are saved.
            </p>
          ) : null}
        </div>
        <div className="mt-3 border-t border-ele-purple-accent pt-2 text-sm">
          <span className="text-ele-body">Margin: </span>
          {summary.margin == null ? (
            <span className="text-ele-body">add cost lines above.</span>
          ) : summary.margin < 0 ? (
            <StatusPill word={`Costs exceed project value (${gbp(summary.margin)})`} tone="amber" />
          ) : (
            <span className="font-bold text-ele-green">{gbp(summary.margin)}{summary.marginPct != null ? ` (${summary.marginPct}%)` : ""}</span>
          )}
        </div>
      </div>

      {/* PAYMENT SCHEDULE (live preview; quiet -- the totals bar carries the headline) */}
      <div className="rounded-ele border border-ele-purple-accent p-4">
        <PaymentSchedule milestones={milestones} contractValue={summary.projectValue} quiet />
        {summary.subcontractorCost + summary.supplierCost > 0 ? (
          <p className="mt-2 text-[11px] text-ele-body opacity-60">The third-party element ({gbp(summary.supplierCost + summary.subcontractorCost)}, supplier + subcontractor cost) is billed in full with order; it is detected automatically from the cost rows. Save costs to apply it.</p>
        ) : null}
      </div>
    </div>
  );
}

// The payment-schedule table, shared by the live editor preview and the read-only view.
// `quiet` (the cost editor, Session AAX Loop 3) drops it to a supporting role: a small
// tracked heading and muted rows, since the pinned totals bar carries the headline figures.
function PaymentSchedule({ milestones, contractValue, quiet }: { milestones: ProjectDetail["milestones"]; contractValue: number | null; quiet?: boolean }) {
  return (
    <div>
      {quiet ? (
        <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Payment schedule</h3>
      ) : (
        <h3 className="mb-2 font-heading text-lg text-ele-strong">Payment schedule</h3>
      )}
      {milestones.length > 0 ? (
        <ul className={`space-y-1 ${quiet ? "text-xs" : "text-sm"}`}>
          {milestones.map((m) => (
            <li key={m.key} className="flex justify-between gap-3">
              <span className="text-ele-body">{m.label}</span>
              <span className={quiet ? "text-ele-body" : "text-ele-strong"}>{gbp(m.amountPence / 100)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ele-body opacity-70">{contractValue == null ? "Set the project value to generate the payment schedule." : "No schedule."}</p>
      )}
    </div>
  );
}

// The named-stage display label for a cached milestone invoice (null = a custom milestone).
function invoiceStageLabel(stage: string | null): string {
  switch (stage) {
    case "with_order":
      return "Payment with order";
    case "third_party_with_order":
      return "Third-party element with order";
    case "balance_on_completion":
      return "Balance on completion";
    default:
      return "Custom";
  }
}

// Milestone invoicing (folded in from the old Billing tab): raise a Xero DRAFT per stage + the
// project's cached invoices. Read from the SAVED project.milestones (the value + third-party).
function CostsInvoicing({ project }: { project: ProjectDetail }) {
  const [invoices, setInvoices] = useState<ProjectInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Named stages with a LIVE (non-void) invoice already raised: shown as such in the
  // dropdown instead of a guaranteed-409 surprise. A stage whose only invoice was voided
  // in Xero is raisable again (the void-and-re-raise correction flow, Session AED).
  const raisedStages = new Set(invoices.filter((inv) => inv.projectStage != null && inv.status !== "void").map((inv) => inv.projectStage as string));
  const stageOptions: { key: MilestoneInvoiceInput["stage"]; label: string; amount: number | null; raised: boolean }[] = [
    ...project.milestones.map((m) => ({ key: m.key, label: m.label, amount: m.amountPence / 100, raised: raisedStages.has(m.key) })),
    { key: "custom" as const, label: "Custom amount", amount: null, raised: false },
  ];
  const [stage, setStage] = useState<MilestoneInvoiceInput["stage"]>(project.milestones[0]?.key ?? "custom");
  const [customAmount, setCustomAmount] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgError, setMsgError] = useState(false);

  const milestoneKeys = project.milestones.map((m) => m.key).join("|");
  useEffect(() => {
    setStage((cur) => (cur === "custom" || project.milestones.some((m) => m.key === cur) ? cur : project.milestones[0]?.key ?? "custom"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestoneKeys]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setInvoices(await api.projects.invoices(project.id));
    } catch {
      setLoadError(true);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);
  useEffect(() => {
    void load();
  }, [load]);

  async function raise() {
    setBusy(true);
    setMsg(null);
    setMsgError(false);
    // Number.isFinite-based parse so a typed "0" reads as the number 0 (rejected with a clear
    // message below) rather than || swallowing it into "no amount given" (Session AED).
    const parsedAmount = customAmount.trim() === "" ? undefined : Number(customAmount);
    if (stage === "custom" && parsedAmount !== undefined && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      setMsgError(true);
      setMsg("Enter an amount greater than zero (ex VAT).");
      setBusy(false);
      return;
    }
    const body: MilestoneInvoiceInput =
      stage === "custom"
        ? { stage, amount: parsedAmount, description: customDesc || undefined }
        : { stage };
    try {
      const res = await api.projects.raiseInvoice(project.id, body);
      // cached=false is rare and serious: the DRAFT exists ONLY in Xero (nothing here will
      // ever list it -- the reconcile refreshes rows already recorded, and discovery only
      // ingests authorised past-due invoices), so the office must review it in Xero and
      // must NOT raise this stage again. The old "it will appear after the next sync"
      // message was false (Session AED).
      setMsg(
        res.cached
          ? "DRAFT invoice raised in Xero (awaiting your review)."
          : "DRAFT invoice raised in Xero, but it could NOT be recorded here and will not appear in this list. Open Xero to review it, and do not raise this stage again (that would create a duplicate).",
      );
      setMsgError(!res.cached);
      await load();
    } catch (e) {
      setMsgError(true);
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not raise the invoice.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-focus-section="delivery-invoicing" className="rounded-ele border border-ele-purple-accent p-4">
      <h3 className="mb-2 font-heading text-lg text-ele-strong">Invoicing</h3>
      <p className="mb-2 text-xs text-ele-body">Staged payments. Every invoice is created in Xero as a DRAFT for your review; nothing is authorised automatically.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-48">
          <label className={labelCls}>Milestone</label>
          <select className={inputCls} value={stage} onChange={(e) => setStage(e.target.value as MilestoneInvoiceInput["stage"])}>
            {stageOptions.map((s) => (
              <option key={s.key} value={s.key}>{s.label}{s.amount != null ? ` (${gbp(s.amount)})` : ""}{s.raised ? " | already invoiced" : ""}</option>
            ))}
          </select>
        </div>
        {stage === "custom" ? (
          <>
            <div><label className={labelCls}>Amount (ex VAT)</label><input type="number" className={inputCls} value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} /></div>
            <div><label className={labelCls}>Description</label><input className={inputCls} value={customDesc} onChange={(e) => setCustomDesc(e.target.value)} /></div>
          </>
        ) : null}
        <Button onClick={raise} disabled={busy || stageOptions.some((s) => s.key === stage && s.raised)} size="sm">{busy ? "Raising..." : "Raise DRAFT"}</Button>
      </div>
      {stageOptions.some((s) => s.key === stage && s.raised) ? (
        <p className="mt-2 text-sm text-amber-300">This stage already has a live invoice (listed below). To re-raise it, void that invoice in Xero first; it becomes available again after the next sync.</p>
      ) : null}
      {msg ? <p role={msgError ? "alert" : undefined} className={`mt-2 rounded-ele px-3 py-2 text-sm ${msgError ? "border border-ele-danger bg-red-500/10 text-ele-danger" : "text-amber-300"}`}>{msg}</p> : null}

      <div className="mt-4">
        <h4 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Invoices</h4>
        {loading ? (
          <Loader shape="inline" />
        ) : loadError ? (
          <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-ele-strong">Could not load the invoices. Please try again in a moment.</p>
        ) : invoices.length === 0 ? (
          <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center text-ele-body">No invoices raised yet.</p>
        ) : (
          <div data-safetynet className="overflow-x-auto rounded-ele border border-ele-purple-accent">
            <table className="w-full text-left text-sm">
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr><th className="px-3 py-2">Stage</th><th className="max-md:hidden px-3 py-2">Contact</th><th className="px-3 py-2">Amount</th><th className="max-md:hidden px-3 py-2">Due</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-ele-purple-accent">
                    <td className="px-3 py-2 text-ele-body">{invoiceStageLabel(inv.projectStage)}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-strong">{inv.contactName ?? "-"}</td>
                    <td className="px-3 py-2 text-ele-body">{gbp(inv.amount)}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">{formatDate(inv.dueDate)}</td>
                    <td className="px-3 py-2"><span className="rounded-full bg-ele-inset px-2 py-0.5 text-[10px] uppercase text-ele-body">{inv.status}</span></td>
                    <td className="px-3 py-2">
                      <a href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(inv.xeroInvoiceId)}`} target="_blank" rel="noopener noreferrer" className="text-ele-green hover:underline">View in Xero</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineCard({ project }: { project: ProjectDetail }) {
  const events: { label: string; date: string | null }[] = [
    { label: "Enquiry received", date: project.createdAt },
    { label: "Quotation issued", date: project.quotedAt },
    { label: "Client accepted", date: project.acceptedAt },
    { label: "BSR Gateway 2 approval", date: project.bsrGateway2Date },
    { label: "Works start (planned)", date: project.startDate },
    { label: "Target completion", date: project.targetCompletion },
    { label: "Completed", date: project.completedAt },
  ];
  return (
    <div className="rounded-ele border border-ele-purple-accent p-4">
      <h3 className="mb-3 font-heading text-lg text-ele-strong">Programme</h3>
      <ol className="space-y-3">
        {events.map((e, i) => (
          <li key={i} className="flex items-center gap-3">
            <span className={`h-2.5 w-2.5 flex-none rounded-full ${e.date ? "bg-ele-green" : "bg-ele-purple-accent"}`} />
            <span className="w-56 text-sm text-ele-body">{e.label}</span>
            <span className="text-sm text-ele-strong">{formatDate(e.date)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
